<template>
    <div class="pb-12 w-full h-screen flex flex-col flex-1 relative">
        <div class="flex-1 flex flex-col">
            <DetailViewHeader
                :title="upperFirst(targetCustomerUser?.name || '')"
                :options="detailViewHeader.options"
                :on-back="() => $router.go(-1)"
                :backLabel="detailViewHeader.backLabel"
                :actionUrl="'#'"
                :actionLabel="'Opportunity Map'"
                :badgeText="
                    convertEnumToRead(targetCustomerUser?.targetCustomer?.type)
                "
                :isActive="detailViewHeader.isActive"
                customClass="mx-2 sm:mx-6"
                class="mt-4"
                @onClickItem="onClickMenu"
                @onActionClick="goToOpportunityMap"
            />
            <Loader v-if="isPageLoading" :isLoading="isPageLoading" />
            <div v-else class="flex flex-col flex-1">
                <SubNavigationBar
                    @onSelect="(item) => (selectedNav = item)"
                    :tabs="getTabItems()"
                    :currentTab="selectedNav"
                    customClass="mt-4 sm:mt-0 mx-0 sm:mx-8"
                />
                <div v-if="selectedNav?.name === 'Details'">
                    <div
                        v-if="completedSurveys?.length > 0"
                        class="sm:mx-8 grid grid-cols-1 lg:grid-cols-7 gap-x-7 pt-10"
                    >
                        <div class="col-span-2">
                            <div class="flex justify-between items-center">
                                <Text
                                    size="xl"
                                    weight="semibold"
                                    color="gray-900"
                                    custom-class="md:font-bold"
                                    :content="'Demographic Data'"
                                />
                            </div>
                            <div>
                                <p class="leading-4 mt-4">
                                    <Text
                                        size="xs"
                                        color="black"
                                        weight="base"
                                        :content="`Now that you have survey data, here is a summary of how each person responded about their demographic info if they chose to do so. Make sure that the demographics of your responders matches the demographics of your Target Customer definition. If they don't, you should either reach out to a more representative group OR modify your definition and estimated market size to match.`"
                                        element="span"
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="col-span-5">
                            <div class="h-fit rounded-md">
                                <DemographicDataSummary
                                    :title="'Demographic Data'"
                                    :demographics="demographics"
                                />
                            </div>
                        </div>
                    </div>
                    <!-- <div v-if="completedSurveys?.length > 0" class="sm:px-8 mt-4 -mb-40">
				<DemographicDataSummary :title="'Demographic Data'" :demographics="demographics" />
			</div> -->
                    <FormFieldGroup
                        @onSave="onSaveName"
                        :items="{ ...formFieldGroup }"
                        :isLoading="savingPart === 'Detail' && isLoading"
                        class="sm:px-8 lg:px-0"
                    />
                    <!-- Contact Sources Section -->
                    <div
                        class="sm:mx-8 grid grid-cols-1 lg:grid-cols-7 gap-x-7 pt-10"
                    >
                        <div class="col-span-2">
                            <div class="flex justify-between items-center">
                                <Text
                                    size="xl"
                                    weight="semibold"
                                    color="gray-900"
                                    custom-class="md:font-bold"
                                    :content="'Contact Sources'"
                                />
                            </div>
                            <div>
                                <p class="leading-4 mt-4">
                                    <Text
                                        size="xs"
                                        color="black"
                                        weight="base"
                                        :content="`These are the places we've identified as good starting places to build out your contact list`"
                                        element="span"
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="col-span-5">
                            <div class="h-fit rounded-md">
                                <div
                                    class="px-5 py-8 grid grid-cols-1 gap-y-4 bg-white border-t border-l border-r border-gray-300 rounded-t-md"
                                >
                                    <!-- Contact Sources List -->
                                    <div class="space-y-4">
                                        <div
                                            v-for="(
                                                platformGroup, platform
                                            ) in groupedOutreachAreas"
                                            :key="platform"
                                            class="flex items-center justify-between border border-gray-200 rounded-lg p-4"
                                        >
                                            <div class="flex items-center">
                                                <span
                                                    class="font-medium text-gray-900 mr-4"
                                                    >{{ platform }}</span
                                                >
                                                <span
                                                    v-if="
                                                        platformGroup.some(
                                                            (area) =>
                                                                area.needsAuth
                                                        )
                                                    "
                                                    class="text-sm text-gray-500"
                                                >
                                                    (needs authentication)
                                                </span>
                                            </div>
                                            <div
                                                class="flex items-center space-x-3"
                                            >
                                                <span
                                                    v-if="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ]
                                                    "
                                                    class="text-sm text-gray-600"
                                                >
                                                    {{
                                                        contactCounts[
                                                            platform
                                                        ] || 0
                                                    }}
                                                    contacts found
                                                </span>
                                                <a
                                                    v-if="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ] &&
                                                        loadingStates[platform]
                                                    "
                                                    @click="
                                                        (event) => {
                                                            event.preventDefault();
                                                            cancelContactSearch(
                                                                platform
                                                            );
                                                        }
                                                    "
                                                    href="#"
                                                    class="text-red-600 underline text-sm cursor-pointer"
                                                >
                                                    Cancel
                                                </a>
                                                <a
                                                    v-if="
                                                        !hasStartedContactSearch[
                                                            platform
                                                        ]
                                                    "
                                                    :href="
                                                        platformGroup[0]
                                                            ?.link || '#'
                                                    "
                                                    class="text-primary-900 underline text-sm"
                                                >
                                                    Do it yourself
                                                </a>
                                                <Button
                                                    v-if="
                                                        !allPlatformsHaveContacts
                                                    "
                                                    :content="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ] &&
                                                        !loadingStates[
                                                            platform
                                                        ] &&
                                                        (contactCounts[
                                                            platform
                                                        ] || 0) > 0
                                                            ? 'Reach out'
                                                            : platformGroup.some(
                                                                  (area) =>
                                                                      area.needsAuth
                                                              )
                                                            ? 'Authorize'
                                                            : 'Find Contacts'
                                                    "
                                                    variant="primary"
                                                    size="sm"
                                                    class="h-8"
                                                    :isLoading="
                                                        loadingStates[platform]
                                                    "
                                                    @click="
                                                        findContacts(
                                                            platform,
                                                            platformGroup
                                                        )
                                                    "
                                                />
                                                <span
                                                    v-else
                                                    class="text-sm text-green-600 font-medium"
                                                >
                                                    ✓ Contacts found
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Fallback if no research areas -->
                                        <div
                                            v-if="
                                                !targetCustomerUser?.outreachAreas ||
                                                targetCustomerUser.outreachAreas
                                                    .length === 0
                                            "
                                            class="text-gray-500 text-center py-8"
                                        >
                                            No contact sources configured yet.
                                        </div>
                                    </div>

                                    <!-- Footer Note -->
                                    <div
                                        class="mt-4 pt-4 border-t border-gray-200"
                                    >
                                        <Text
                                            size="xs"
                                            color="gray-600"
                                            :content="`You can always add your own contacts from any source on your `"
                                            element="span"
                                        />
                                        <router-link
                                            :to="`/venture/${$route.params.ventureId}/target-customers/${$route.params.targetCustomerId}/contacts`"
                                            class="text-primary-600 underline text-xs"
                                        >
                                            Contacts page
                                        </router-link>
                                        <Text
                                            size="xs"
                                            color="gray-600"
                                            :content="`.`"
                                            element="span"
                                        />
                                    </div>
                                </div>
                                <div
                                    class="flex flex-row justify-between w-full bg-gray-100 border border-gray-300 rounded-b-md"
                                >
                                    <div />
                                    <div
                                        class="flex flex-row items-end justify-end w-1/4 my-4 mr-4 sm:mr-3 lg:mr-5"
                                    >
                                        <!-- No action buttons needed for Contact Sources -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Research Prompt Section -->
                    <div
                        class="sm:mx-8 grid grid-cols-1 lg:grid-cols-7 gap-x-7 pt-10"
                    >
                        <div class="col-span-2">
                            <div class="flex justify-between items-center">
                                <Text
                                    size="xl"
                                    weight="semibold"
                                    color="gray-900"
                                    custom-class="md:font-bold"
                                    :content="'Research Prompt'"
                                />
                            </div>
                            <div>
                                <p class="leading-4 mt-4">
                                    <Text
                                        size="xs"
                                        color="black"
                                        weight="base"
                                        :content="`This summarizes your research and will help generate your interview script and survey`"
                                        element="span"
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="col-span-5">
                            <div class="h-fit rounded-md">
                                <div
                                    class="px-5 py-8 grid grid-cols-1 gap-y-4 bg-white border-t border-l border-r border-gray-300 rounded-t-md"
                                >
                                    <div class="w-full">
                                        <Input
                                            :label="`We are learning about the real experiences of ${(
                                                targetCustomerUser?.name || ''
                                            )?.toLowerCase()} and how they...`"
                                            v-model:value="formData.research"
                                        />
                                    </div>
                                    <AccordionWithList
                                        :accordion="{
                                            name: 'Research Recommendations',
                                        }"
                                        leading
                                        hideBottom
                                    >
                                        <div
                                            class="py-3 px-3 grid grid-cols-2 gap-2"
                                        >
                                            <div>
                                                <Text
                                                    size="sm"
                                                    color="gray-500"
                                                    content="Market Count and Demographics Sources"
                                                />
                                                <div class="mt-2">
                                                    <Text
                                                        size=""
                                                        color="black"
                                                        :content="`- Google &ldquo;Number of ${targetCustomerUser?.name} in US&rdquo;`"
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://datausa.io' target='_blank' class='text-indigo-500'>datausa.io</a>`
                                                        "
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://census.gov' target='_blank' class='text-indigo-500'>census.gov</a>`
                                                        "
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://bls.gov' target='_blank' class='text-indigo-500'>bls.gov</a>`
                                                        "
                                                    />
                                                </div>

                                                <div class="my-6">
                                                    <Text
                                                        size=""
                                                        color="black"
                                                        custom-class=""
                                                        :content="`Further Research`"
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-if="
                                                            targetCustomerUser
                                                                ?.targetCustomer
                                                                ?.type !==
                                                            'INDIVIDUALS'
                                                        "
                                                        v-html="
                                                            `- <a href='https://statista.com' target='_blank' class='text-indigo-500'>statista.com</a>`
                                                        "
                                                    />
                                                    <div v-else>
                                                        <Text
                                                            size="sm"
                                                            color="black"
                                                            v-html="
                                                                `- <a href='http://mintel.com/' target='_blank' class='text-indigo-500'>mintel.com</a>`
                                                            "
                                                        />
                                                        <Text
                                                            size="sm"
                                                            color="black"
                                                            v-html="
                                                                `- <a href='https://audiences.dstillery.com/' target='_blank' class='text-indigo-500'>audiences.dstillery.com</a>`
                                                            "
                                                        />
                                                        <Text
                                                            size="sm"
                                                            color="black"
                                                            v-html="
                                                                `- <a href='https://www.trendhunter.com/' target='_blank' class='text-indigo-500'>trendhunter.com</a>`
                                                            "
                                                        />
                                                    </div>
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://answerthepublic.com' target='_blank' class='text-indigo-500'>answerthepublic.com</a>`
                                                        "
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://thinkwithgoogle.com' target='_blank' class='text-indigo-500'>thinkwithgoogle.com</a>`
                                                        "
                                                    />
                                                </div>
                                                <Button
                                                    @click="onGoogle"
                                                    content="Trying Googling It"
                                                    variant=""
                                                    custom-class="bg-indigo-400 text-white"
                                                />
                                            </div>
                                            <div>
                                                <Text
                                                    size="sm"
                                                    color="gray-500"
                                                    content="Contact List Sources"
                                                />
                                                <div class="mt-2">
                                                    <Text
                                                        size=""
                                                        color="black"
                                                        :content="`- LinkedIn`"
                                                    />
                                                    <Text
                                                        size="sm"
                                                        color="black"
                                                        v-html="
                                                            `- <a href='https://seamless.ai' target='_blank' class='text-indigo-500'>Seamless.ai</a>`
                                                        "
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionWithList>
                                </div>
                                <div
                                    class="flex flex-row justify-between w-full bg-gray-100 border border-gray-300 rounded-b-md"
                                >
                                    <div />
                                    <div
                                        class="flex flex-row items-end justify-end w-1/4 my-4 mr-4 sm:mr-3 lg:mr-5"
                                    >
                                        <Button
                                            content="Cancel"
                                            :variant="null"
                                            customClass="bg-white text-black underline px-2 bg-gray-100"
                                        />
                                        <Button
                                            content="Save"
                                            variant="primary"
                                            customClass="ml-4 px-4"
                                            :isLoading="
                                                isLoading &&
                                                savingPart === 'Research'
                                            "
                                            @click="onSaveResearch"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="selectedNav?.name === 'Opportunities'"
                    class="sm:px-8 mt-4"
                >
                    <!-- Custom header with both buttons -->
                    <div class="flex flex-col p-4 -mx-4">
                        <div
                            class="flex items-start lg:items-start justify-between gap-2"
                        >
                            <div
                                class="flex items-start lg:items-start flex-1 gap-2 mr-2 mt-1.5 lg:mt-0"
                            >
                                <Text
                                    color="black"
                                    weight="bold"
                                    size="xl"
                                    custom-class="sm:text-xl"
                                    content="Opportunities"
                                />
                                <Badge
                                    v-if="opportunities?.length"
                                    :label="opportunities?.length.toString()"
                                    px="3"
                                    textColor="gray-600"
                                    bgColor="gray-300"
                                    py="1"
                                    isRounded
                                    textSize="lg"
                                    textWeight="bold"
                                    customClass="flex-shrink-0"
                                />
                            </div>
                            <div class="flex gap-2 items-center">
                                <!-- Search box -->
                                <InputWithLeadingIcon
                                    placeholder="Search"
                                    icon="SearchIcon"
                                    class="hidden lg:block min-w-min"
                                    v-model:value="searchText"
                                />
                                <!-- Add Opportunity Button -->
                                <Button
                                    @click="() => (isShowOpportunity = true)"
                                    content="Add Opportunity"
                                    variant="primary"
                                    size="md"
                                    class="h-10 lg:h-10"
                                />
                            </div>
                        </div>
                        <!-- Mobile search box -->
                        <InputWithLeadingIcon
                            placeholder="Search"
                            icon="SearchIcon"
                            class="lg:hidden mt-2"
                            v-model:value="searchText"
                        />
                    </div>
                    <div class="space-y-3 mt-5 bg-white">
                        <EmptyState
                            v-if="opportunities?.length === 0"
                            class="bg-white"
                            isFull
                            description="Looks like there aren't any opportunities here!"
                            btnText="Add Opportunity"
                            :action="() => (isShowOpportunity = true)"
                        />
                        <div
                            class="flex flex-col border border-gray-300"
                            v-for="opportunity in getFilterAndSort(
                                opportunities
                            )"
                            :key="opportunity.id"
                        >
                            <Items
                                v-bind="opportunity"
                                @onClick="
                                    () =>
                                        $router.push({
                                            name: 'Venture Opportunity Detail',
                                            params: {
                                                ventureId: venture?.id,
                                                id: opportunity?.id,
                                            },
                                        })
                                "
                            />
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="selectedNav?.name === 'Contacts'"
                    class="sm:px-8 mt-4"
                >
                    <ListViewHeader
                        title="Contacts"
                        btn-text="Add Contact"
                        @onClick="() => (isShow = true)"
                    />
                    <div class="space-y-3 mt-5">
                        <EmptyState
                            v-if="contacts?.length === 0"
                            class="bg-white"
                            isFull
                            v-bind="emptyContact"
                            :action="
                                () => {
                                    isShow = true;
                                }
                            "
                        />
                        <SortTable
                            v-if="contacts?.length > 0"
                            :list="contacts"
                            :headers="headers"
                            @onClickItem="
                                (item) =>
                                    $router.push({
                                        name: 'Venture Contact Detail',
                                        params: {
                                            ventureId: $route.params.ventureId,
                                            id: item?.id,
                                        },
                                    })
                            "
                            isClickable
                            isRow
                            isMultipleSelection
                            @onSelected="
                                (selected, callback) => {
                                    selectedItems = selected;
                                    onDone = callback;
                                }
                            "
                        />
                    </div>
                    <div class="gap-6 md:flex item-center mt-5">
                        <Text
                            v-if="selectedItems.length > 0 && !isDeleting"
                            size="sm"
                            color="rose-600"
                            content="Archive Selected"
                            decoration="underline"
                            custom-class="cursor-pointer"
                            style="margin-top: 2px"
                            @click="() => onArchive()"
                        />
                        <svg
                            v-if="isDeleting"
                            class="w-5 h-5 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-20"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-100"
                                fill="#574CCB"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <div v-for="link in links" :key="link.content">
                            <a
                                class="text-sm text-indigo-700 underline font-semibold"
                                :content="link.content"
                                size="sm"
                                :decoration="link.decoration"
                                color="indigo-700"
                                @click="link.action"
                                >{{ link.content }}</a
                            >
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="selectedNav?.name === 'Outreach'"
                    class="sm:px-8 mt-4"
                >
                    <div
                        id="outreach_template"
                        class="sm:mx-8 grid grid-cols-1 lg:grid-cols-7 gap-x-7 pt-10"
                    >
                        <div class="col-span-2">
                            <div class="flex justify-between items-center">
                                <Text
                                    size="xl"
                                    weight="semibold"
                                    color="gray-900"
                                    custom-class="md:font-bold"
                                    :content="'Outreach Templates'"
                                />
                            </div>
                            <div>
                                <p class="leading-4 mt-4">
                                    <Text
                                        size="xs"
                                        color="black"
                                        weight="base"
                                        :content="`Create and manage email templates for reaching out to your target customers`"
                                        element="span"
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="col-span-5">
                            <div class="shadow-lg h-fit rounded-md">
                                <div
                                    class="px-5 py-8 grid grid-cols-1 gap-y-4 bg-white border-t border-l border-r border-gray-300 rounded-t-md"
                                >
                                    <div
                                        v-if="selectedTab"
                                        class="w-full space-y-3"
                                    >
                                        <div
                                            class="flex flex-wrap items-center space-x-4"
                                        >
                                            <a
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                @click="selectedTab = tab"
                                                class="py-2 text-xs font-semibold text-black border-b-2"
                                                :class="
                                                    selectedTab &&
                                                    selectedTab.id === tab.id
                                                        ? 'border-black'
                                                        : 'border-white'
                                                "
                                            >
                                                {{ tab.name }}
                                            </a>
                                        </div>
                                        <Input
                                            :label="`Subject`"
                                            v-model:value="selectedTab.subject"
                                        />
                                        <editor-content
                                            :editor="editor"
                                            class="px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                        <div>
                                            <Text
                                                size="xs"
                                                content="{{firstName}} adds your contact's name in the message"
                                                custom-class="italic"
                                            />
                                            <Text
                                                v-if="
                                                    selectedTab?.origBody?.includes(
                                                        'targetCustomer'
                                                    )
                                                "
                                                size="xs"
                                                v-html="
                                                    `{{targetCustomerUser.name}} adds in <b>${targetCustomerUser?.name}</b>`
                                                "
                                                custom-class="italic"
                                            />
                                            <Text
                                                v-if="
                                                    selectedTab?.origBody?.includes(
                                                        'researchPrompt'
                                                    )
                                                "
                                                size="xs"
                                                v-html="
                                                    `{{researchPrompt}} adds in <b>${targetCustomerUser?.researchPrompt}</b>`
                                                "
                                                custom-class="italic"
                                            />
                                            <Text
                                                v-if="
                                                    selectedTab?.origBody?.includes(
                                                        'interviewTime + timezone'
                                                    )
                                                "
                                                size="xs"
                                                v-html="
                                                    `{{interviewTime + timezone}} adds in ${
                                                        targetCustomerUser
                                                            ?.interviews?.[0]
                                                            ?.scheduledDateTime
                                                            ? `<b>${moment(
                                                                  targetCustomerUser
                                                                      ?.interviews?.[0]
                                                                      ?.scheduledDateTime
                                                              ).format(
                                                                  'MM/DD/YY h:mm'
                                                              )}</b>`
                                                            : 'the date and time of the interview'
                                                    }`
                                                "
                                                custom-class="italic"
                                            />
                                            <Text
                                                v-if="
                                                    selectedTab?.origBody?.includes(
                                                        'contactSurveyLink'
                                                    )
                                                "
                                                size="xs"
                                                v-html="
                                                    `{{contactSurveyLink}} adds in the contact\'s unique survey URL`
                                                "
                                                custom-class="italic"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="flex flex-row justify-between w-full bg-gray-100 border border-gray-300 rounded-b-none sm:rounded-b-md"
                                >
                                    <div />
                                    <div
                                        class="flex flex-row items-end justify-end w-1/4 my-4 mr-4 sm:mr-3 lg:mr-5"
                                    >
                                        <Button
                                            content="Cancel"
                                            :variant="null"
                                            customClass="bg-white text-black underline px-2 bg-gray-100"
                                        />
                                        <Button
                                            @click="onSaveEmailTemplate"
                                            content="Save"
                                            variant="primary"
                                            customClass="ml-4 px-4"
                                            :isLoading="
                                                isLoading &&
                                                savingPart === 'EmailTemplate'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Outreach Rewards Section -->
                    <div
                        class="sm:mx-8 grid grid-cols-1 lg:grid-cols-7 gap-x-7 pt-12"
                    >
                        <div class="col-span-2">
                            <div class="flex justify-between items-center">
                                <Text
                                    size="xl"
                                    weight="semibold"
                                    color="gray-900"
                                    custom-class="md:font-bold"
                                    :content="'Outreach Rewards'"
                                />
                            </div>
                            <div>
                                <p class="leading-4 mt-4">
                                    <Text
                                        size="xs"
                                        color="black"
                                        weight="base"
                                        :content="`Set monetary incentives for interviews and surveys to encourage participation`"
                                        element="span"
                                    />
                                </p>
                            </div>
                        </div>
                        <div class="col-span-5">
                            <div class="shadow-lg h-fit rounded-md">
                                <div
                                    class="px-5 py-8 grid grid-cols-1 gap-y-4 bg-white border-t border-l border-r border-gray-300 rounded-t-md"
                                >
                                    <!-- Interviews Section -->
                                    <div class="space-y-4">
                                        <div class="flex items-center">
                                            <Text
                                                size="lg"
                                                weight="semibold"
                                                color="gray-900"
                                                :content="'Interviews (at least 12)'"
                                            />
                                        </div>
                                        <div class="grid grid-cols-2 gap-4">
                                            <div>
                                                <Text
                                                    size="sm"
                                                    weight="medium"
                                                    color="gray-700"
                                                    :content="'Interview Reward'"
                                                />
                                                <div class="relative mt-1">
                                                    <div
                                                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                                    >
                                                        <span
                                                            class="text-gray-500 sm:text-sm"
                                                            >$</span
                                                        >
                                                    </div>
                                                    <Input
                                                        v-model:value="
                                                            interviewReward
                                                        "
                                                        placeholder="20"
                                                        class="pl-7"
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Text
                                                    size="sm"
                                                    weight="medium"
                                                    color="gray-700"
                                                    :content="'Max Interview Rewards'"
                                                />
                                                <Input
                                                    v-model:value="
                                                        maxInterviewRewards
                                                    "
                                                    placeholder="20"
                                                    class="mt-1"
                                                    type="number"
                                                    min="20"
                                                />
                                                <Text
                                                    size="xs"
                                                    color="gray-500"
                                                    :content="'Minimum: 20'"
                                                    custom-class="mt-1"
                                                />
                                            </div>
                                        </div>
                                        <div class="bg-gray-50 p-3 rounded-md">
                                            <Text
                                                size="sm"
                                                weight="medium"
                                                color="gray-900"
                                                :content="`= $${interviewBudget} Interview Budget`"
                                            />
                                        </div>
                                        <div class="bg-blue-50 p-3 rounded-md">
                                            <Text
                                                size="xs"
                                                color="blue-800"
                                                :content="`I'm offering $${interviewReward} to the first ${maxInterviewRewards} people that are willing to talk.`"
                                            />
                                        </div>
                                    </div>

                                    <!-- Surveys Section -->
                                    <div
                                        class="space-y-4 pt-4 border-t border-gray-200"
                                    >
                                        <div class="flex items-center">
                                            <Text
                                                size="lg"
                                                weight="semibold"
                                                color="gray-900"
                                                :content="'Surveys (at least 30)'"
                                            />
                                        </div>
                                        <div class="grid grid-cols-2 gap-4">
                                            <div>
                                                <Text
                                                    size="sm"
                                                    weight="medium"
                                                    color="gray-700"
                                                    :content="'Survey Reward'"
                                                />
                                                <div class="relative mt-1">
                                                    <div
                                                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                                    >
                                                        <span
                                                            class="text-gray-500 sm:text-sm"
                                                            >$</span
                                                        >
                                                    </div>
                                                    <Input
                                                        v-model:value="
                                                            surveyReward
                                                        "
                                                        placeholder="0"
                                                        class="pl-7"
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Text
                                                    size="sm"
                                                    weight="medium"
                                                    color="gray-700"
                                                    :content="'Max Survey Rewards'"
                                                />
                                                <Input
                                                    v-model:value="
                                                        maxSurveyRewards
                                                    "
                                                    placeholder="30"
                                                    class="mt-1"
                                                    type="number"
                                                    min="30"
                                                />
                                                <Text
                                                    size="xs"
                                                    color="gray-500"
                                                    :content="'Minimum: 30'"
                                                    custom-class="mt-1"
                                                />
                                            </div>
                                        </div>
                                        <div class="bg-gray-50 p-3 rounded-md">
                                            <Text
                                                size="sm"
                                                weight="medium"
                                                color="gray-900"
                                                :content="`= $${surveyBudget} Survey Budget`"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="flex flex-row justify-between w-full bg-gray-100 border border-gray-300 rounded-b-none sm:rounded-b-md"
                                >
                                    <div />
                                    <div
                                        class="flex flex-row items-end justify-end w-1/4 my-4 mr-4 sm:mr-3 lg:mr-5"
                                    >
                                        <Button
                                            content="Cancel"
                                            :variant="null"
                                            customClass="bg-white text-black underline px-2 bg-gray-100"
                                        />
                                        <Button
                                            @click="saveOutreachRewards"
                                            content="Save"
                                            variant="primary"
                                            customClass="ml-4 px-4"
                                            :isLoading="
                                                isLoading &&
                                                savingPart === 'OutreachRewards'
                                            "
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="selectedNav?.name === 'Interviews'"
                    class="sm:px-8 mt-4 space-y-4"
                >
                    <div class="bg-gray-100 p-4">
                        <Text
                            size="lg"
                            weight="semibold"
                            color="black"
                            :content="`Completed Interviews (${
                                interviews?.length || 0
                            })`"
                        />
                        <Table
                            :list="interviews"
                            @onClickItem="
                                (item) =>
                                    $router.push({
                                        name: 'Venture Contact Detail',
                                        params: {
                                            ventureId: $route.params?.ventureId,
                                            id: item?.id,
                                        },
                                    })
                            "
                            isClickable
                        >
                        </Table>
                    </div>
                    <div class="space-y-3" v-if="selectedInterviewScript?.id">
                        <div class="bg-gray-100 p-4">
                            <div class="flex justify-between items-center">
                                <div class="flex space-x-4 items-center">
                                    <Text
                                        v-if="!isEditScriptName"
                                        size="lg"
                                        weight="semibold"
                                        color="black"
                                        :content="selectedInterviewScript?.name"
                                    />
                                    <Input
                                        v-else
                                        v-model:value="
                                            selectedInterviewScript.name
                                        "
                                        @blur="
                                            () =>
                                                onSaveScriptName(
                                                    selectedInterviewScript
                                                )
                                        "
                                        @keyup.enter="onPressScript"
                                        :id="selectedInterviewScript?.id"
                                        custom-class="font-semibold text-lg w-80"
                                    />
                                    <div class="relative">
                                        <Icon
                                            name="ChevronDownIcon"
                                            size="5"
                                            is-outline
                                            color="gray-600"
                                            custom-class="cursor-pointer"
                                            @click="isShowInterviewList = true"
                                        />
                                        <div
                                            v-if="isShowInterviewList"
                                            v-click-outside="
                                                () =>
                                                    (isShowInterviewList = false)
                                            "
                                            class="absolute z-10 border border-gray-300 bg-white py-1 w-56"
                                        >
                                            <button
                                                @click.stop="
                                                    () => {
                                                        selectedInterviewScript =
                                                            item;
                                                        isShowInterviewList = false;
                                                    }
                                                "
                                                v-for="item in targetCustomerUser?.interviewScripts"
                                                :key="item.id"
                                                class="px-2 py-1 hover:bg-gray-100 w-full space-x-2 flex justify-between items-center"
                                                :class="
                                                    item?.id ===
                                                    selectedInterviewScript?.id
                                                        ? 'bg-gray-200'
                                                        : ''
                                                "
                                            >
                                                <Text
                                                    size="xs"
                                                    color="black"
                                                    :content="item?.name"
                                                    custom-class="flex flex-1 w-full truncate"
                                                />
                                                <Icon
                                                    @click.stop="
                                                        () => {
                                                            onDeleteScript(
                                                                selectedInterviewScript
                                                            );
                                                            isShowInterviewList = false;
                                                        }
                                                    "
                                                    name="TrashIcon"
                                                    color="rose-600"
                                                    size="5"
                                                    custom-class="cursor-pointer"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex space-x-4">
                                    <Text
                                        @click="
                                            () =>
                                                onEditScriptName(
                                                    selectedInterviewScript
                                                )
                                        "
                                        color="gray-600"
                                        custom-class="cursor-pointer"
                                        decoration="underline"
                                        content="rename"
                                    />
                                    <Text
                                        @click="
                                            () =>
                                                onDeleteScript(
                                                    selectedInterviewScript
                                                )
                                        "
                                        color="red-600"
                                        custom-class="cursor-pointer"
                                        decoration="underline"
                                        content="delete"
                                    />
                                    <div class="flex items-center space-x-2">
                                        <Text
                                            @click="onCreateInterviewScript"
                                            color="indigo-600"
                                            custom-class="cursor-pointer"
                                            decoration="underline"
                                            content="+ script"
                                        />
                                        <svg
                                            v-if="isAdding"
                                            class="w-5 h-5 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                class="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                stroke-width="4"
                                            ></circle>
                                            <path
                                                class="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <Text
                                size="xs"
                                weight="medium"
                                content="The content below has been auto-generated based on your research prompt. Edit it however you'd like and make sure it reads naturally."
                                custom-class="italic"
                                color="gray-600"
                            />
                            <div
                                class="mt-5 space-y-4"
                                v-for="(interview, i) in getInterviewAccordions(
                                    selectedInterviewScript
                                )"
                                :key="interview.id"
                                :id="interview?._id"
                            >
                                <AccordionWithList
                                    :isInitialOpen="activeIndex === i"
                                    :accordion="interview"
                                    isEditor
                                    @onOpen="activeIndex = i"
                                    :isLoading="
                                        isLoading && savingPart === 'Interview'
                                    "
                                    @onSaveEditor="
                                        (content) =>
                                            (interview.content = content)
                                    "
                                    @onSave="
                                        () =>
                                            onSaveInterview(
                                                interview,
                                                selectedInterviewScript
                                            )
                                    "
                                >
                                    <template #help>
                                        <Text
                                            size="xs"
                                            content="{{firstName}} adds your contact's name in the message"
                                            custom-class="italic"
                                        />
                                        <Text
                                            size="xs"
                                            v-html="
                                                `{{targetCustomerUser.name}} adds in <b>${targetCustomerUser?.name}</b>`
                                            "
                                            custom-class="italic"
                                        />
                                        <Text
                                            size="xs"
                                            v-html="
                                                `{{researchPrompt}} adds in <b>${targetCustomerUser?.researchPrompt}</b>`
                                            "
                                            custom-class="italic"
                                        />
                                        <Text
                                            size="xs"
                                            v-html="
                                                `You can style the text using <a href='https://www.markdownguide.org/cheat-sheet/' target='blank' class='underline text-indigo-600'>markdown syntax</a>`
                                            "
                                            custom-class="mt-3 italic"
                                        />
                                    </template>
                                </AccordionWithList>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="selectedNav?.name === 'Surveys'"
                    class="sm:px-8 mt-4 space-y-4"
                >
                    <EmptyState
                        v-if="targetCustomerUser?.surveys?.length === 0"
                        class="bg-white"
                        isFull
                        :icon="emptyViewBinding.icon"
                        :title="emptyViewBinding.title"
                        :description="emptyViewBinding.description"
                        :btnText="emptyViewBinding.btnText"
                        :action="() => onAddSurvey()"
                        :isLoading="isLoading"
                    />
                    <div
                        v-if="completedSurveys?.length > 0"
                        class="bg-gray-100 p-4"
                    >
                        <Text
                            size="lg"
                            weight="semibold"
                            color="black"
                            :content="`Completed Surveys (${completedSurveys?.length})`"
                        />
                        <Table
                            :list="completedSurveys"
                            @onClickItem="
                                (item) =>
                                    $router.push({
                                        name: 'Venture Contact Detail',
                                        params: {
                                            ventureId: $route.params?.ventureId,
                                            id: item?.id,
                                        },
                                        query: {
                                            targetCustomerUserId:
                                                targetCustomerUser?.id,
                                        },
                                    })
                            "
                            isClickable
                        >
                            <template #multiselect>
                                <Text
                                    content="View"
                                    decoration="underline"
                                    size="sm"
                                    color="indigo-700"
                                />
                            </template>
                        </Table>
                    </div>
                    <div
                        v-if="targetCustomerUser?.surveys?.length > 0"
                        class="bg-gray-100 p-4"
                    >
                        <div class="flex justify-between items-center">
                            <div class="flex space-x-4 items-center">
                                <Text
                                    v-if="!isEditSurveyName"
                                    size="lg"
                                    weight="semibold"
                                    color="black"
                                    :content="survey?.name"
                                />
                                <Input
                                    v-else
                                    v-model:value="survey.name"
                                    @blur="onSaveSurveyName"
                                    @keyup.enter="onPressEnter"
                                    :id="'survey-name'"
                                    custom-class="font-semibold text-lg"
                                />
                                <a @click="isShowList = true" class="relative">
                                    <Icon
                                        name="ChevronDownIcon"
                                        size="5"
                                        is-outline
                                        color="gray-600"
                                    />
                                    <div
                                        v-if="isShowList"
                                        v-click-outside="
                                            () => (isShowList = false)
                                        "
                                        class="absolute z-10 border border-gray-300 bg-white py-1 w-40"
                                    >
                                        <button
                                            @click.stop="
                                                () => {
                                                    survey = item;
                                                    isShowList = false;
                                                }
                                            "
                                            v-for="item in targetCustomerUser?.surveys"
                                            :key="item.id"
                                            class="px-2 py-1 hover:bg-gray-100 w-full space-x-2 flex justify-between items-center"
                                            :class="
                                                item?.id === survey?.id
                                                    ? 'bg-gray-200'
                                                    : ''
                                            "
                                        >
                                            <Text
                                                size="xs"
                                                color="black"
                                                :content="item?.name"
                                                custom-class="flex flex-1 w-full truncate"
                                            />
                                            <Icon
                                                @click.stop="
                                                    () => {
                                                        isOpenRemoveSurvey = true;
                                                        isShowList = false;
                                                    }
                                                "
                                                name="TrashIcon"
                                                color="rose-600"
                                                size="5"
                                                custom-class="cursor-pointer"
                                            />
                                        </button>
                                    </div>
                                </a>
                            </div>
                            <div class="flex items-center space-x-4">
                                <Text
                                    @click="onClickEditSurveyName"
                                    color="gray-600"
                                    decoration="underline"
                                    content="rename"
                                    custom-class="cursor-pointer"
                                />
                                <svg
                                    v-if="
                                        isLoading && savingPart === 'ADD_SURVEY'
                                    "
                                    class="w-5 h-5 animate-spin ml-2"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    ></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <Text
                                    v-else
                                    @click="onAddSurvey"
                                    color="indigo-600"
                                    decoration="underline"
                                    content="+ survey"
                                    custom-class="cursor-pointer"
                                />
                            </div>
                        </div>
                        <Text
                            size="xs"
                            weight="medium"
                            content="This is where you can setup the Opportunities you want to measure in your survey and edit all of the content in each section, including the intro, outro, and demographics sections."
                            custom-class="italic"
                            color="gray-600"
                        />
                        <div class="mt-4">
                            <Text
                                size="xs"
                                weight="semibold"
                                color="black"
                                content="Generic Survey Link"
                            />
                            <div
                                v-if="survey?.id"
                                class="border rounded-md border-gray-300 relative px-3 py-2 flex justify-between items-center mt-1"
                            >
                                <input
                                    class="border-none outline-none focus:ring-0 focus:border-none focus:outline-none w-full bg-gray-100 text-sm"
                                    v-model="survey.publicUrl"
                                    disabled
                                    autocomplete="off"
                                />
                                <Icon
                                    name="DocumentDuplicateIcon"
                                    size="6"
                                    color="gray-500"
                                    custom-class="cursor-pointer"
                                    @click="onCopyUrl(survey.publicUrl)"
                                />
                            </div>
                        </div>
                        <div class="mt-5 space-y-4">
                            <AccordionWithList
                                :is-loading="
                                    isLoading && savingPart === 'Survey'
                                "
                                @onSave="onSaveSurvey"
                                :accordion="introAccordion"
                                leading
                            >
                                <div class="p-4 space-y-2">
                                    <Input
                                        label="Heading"
                                        v-model:value="survey.welcomeHeading"
                                    />
                                    <Input
                                        label="Description"
                                        v-model:value="
                                            survey.welcomeDescription
                                        "
                                    />
                                </div>
                                <template #heading>
                                    <Text
                                        @click="
                                            () =>
                                                onClickPreview('INTRO', survey)
                                        "
                                        class="underline cursor-pointer"
                                        :content="'preview'"
                                        color="indigo-500"
                                        hoverColor="indigo-600"
                                        size="sm"
                                        weight="medium"
                                    />
                                </template>
                            </AccordionWithList>
                            <AccordionWithList
                                hide-bottom
                                :accordion="optionAccordion"
                                leading
                            >
                                <div class="p-4 space-y-2">
                                    <Text
                                        size="xs"
                                        color="black"
                                        custom-class="italic"
                                        element="span"
                                        v-html="
                                            `If you send this survey out to a group or someone you don't know fits your target customer definition, we will ask these questions to make sure they fit. If they don't, they will not be able to respond to your survey. <b>These questions are not editable.</b>`
                                        "
                                    />
                                    <div />
                                    <div
                                        v-for="tag in getTargetTags()"
                                        :key="tag.id"
                                        class=""
                                    >
                                        <Text
                                            size="xs"
                                            color="black"
                                            weight="semibold"
                                            :content="`${upperFirst(
                                                tag?.category?.toLowerCase()
                                            )}: ${tag.name}`"
                                        />
                                        <Text
                                            size="xs"
                                            color="black"
                                            weight=""
                                            custom-class="mt-1"
                                            :content="tag.confirmationQuestion"
                                        />
                                    </div>
                                </div>
                                <template #heading>
                                    <Text
                                        @click="
                                            () => onClickPreview('TAG', survey)
                                        "
                                        class="underline cursor-pointer"
                                        :content="'preview'"
                                        color="indigo-500"
                                        hoverColor="indigo-600"
                                        size="sm"
                                        weight="medium"
                                    />
                                </template>
                            </AccordionWithList>
                            <div
                                class="flex justify-between items-center mt-10"
                            >
                                <div class="flex space-x-4 items-center">
                                    <Text
                                        size="lg"
                                        weight="semibold"
                                        color="black"
                                        :content="`Opportunities (${survey?.surveyOpportunities?.length})`"
                                    />
                                    <div class="relative">
                                        <Text
                                            @click="
                                                isShowOpportunitySelect = true
                                            "
                                            v-if="
                                                survey?.surveyOpportunities
                                                    ?.length > 0 &&
                                                survey?.surveyOpportunities
                                                    ?.length < 3
                                            "
                                            color="indigo-600"
                                            decoration="underline"
                                            content="select opportunities"
                                            custom-class="cursor-pointer"
                                        />
                                        <div
                                            v-if="
                                                isShowOpportunitySelect &&
                                                survey?.surveyOpportunities
                                                    ?.length > 0
                                            "
                                            v-click-outside="
                                                () => {
                                                    isShowOpportunitySelect = false;
                                                }
                                            "
                                        >
                                            <OpportunityList
                                                :selectedOpportunities="
                                                    selectedOpportunities
                                                "
                                                @onClickItem="
                                                    (opportunity) =>
                                                        onSelectOpportunity(
                                                            opportunity
                                                        )
                                                "
                                                :opportunities="
                                                    opItems?.filter(
                                                        (op) =>
                                                            !survey?.surveyOpportunities?.find(
                                                                (o) =>
                                                                    o
                                                                        ?.opportunity
                                                                        ?.id ===
                                                                    op?.id
                                                            )
                                                    )
                                                "
                                                :isLoading="
                                                    savingPart ===
                                                        'ADD_OPPORTUNITY' &&
                                                    isLoading
                                                "
                                                isNotAdd
                                                custom-class="left-0 top-6"
                                                @onDone="
                                                    () =>
                                                        onCreateSurveyOpportunity()
                                                "
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="survey?.surveyOpportunities?.length === 0"
                                class="flex items-center relative"
                            >
                                <EmptyState
                                    class="bg-white mb-2 mt-2"
                                    isFull
                                    :icon="emptyOpportunity.icon"
                                    :title="emptyOpportunity.title"
                                    :description="emptyOpportunity.description"
                                    :btnText="emptyOpportunity.btnText"
                                    :action="
                                        () => (isShowOpportunitySelect = true)
                                    "
                                />
                                <div
                                    v-if="isShowOpportunitySelect"
                                    v-click-outside="
                                        () => {
                                            isShowOpportunitySelect = false;
                                        }
                                    "
                                >
                                    <OpportunityList
                                        :selectedOpportunities="
                                            selectedOpportunities
                                        "
                                        @onClickItem="
                                            (opportunity) =>
                                                onSelectOpportunity(opportunity)
                                        "
                                        :opportunities="
                                            opItems?.filter(
                                                (op) =>
                                                    !survey?.surveyOpportunities?.find(
                                                        (o) =>
                                                            o?.opportunity
                                                                ?.id === op?.id
                                                    )
                                            )
                                        "
                                        :isLoading="
                                            savingPart === 'ADD_OPPORTUNITY' &&
                                            isLoading
                                        "
                                        isNotAdd
                                        custom-class="left-16 top-40"
                                        @onDone="
                                            () => onCreateSurveyOpportunity()
                                        "
                                    />
                                </div>
                            </div>
                            <Text
                                v-if="survey?.surveyOpportunities?.length > 0"
                                size="xs"
                                weight="medium"
                                content="This is where you can setup the Opportunities you want to measure in your survey and edit all of the content in each section, including the intro, outro, and demographics sections."
                                custom-class="italic"
                                color="gray-600"
                            />
                            <div
                                v-if="survey?.surveyOpportunities?.length > 0"
                                class="mt-5 relative"
                            >
                                <draggable
                                    :list="survey?.surveyOpportunities"
                                    itemKey="draggable"
                                    :sort="true"
                                    :disabled="
                                        !!survey?.surveyOpportunities?.find(
                                            (s) => s?.isExpanded
                                        )?.id
                                    "
                                    @end="(e) => onDragEnd(e)"
                                    class="space-y-4"
                                >
                                    <template
                                        #item="{
                                            element: surveyOpportunity,
                                            index: i,
                                        }"
                                    >
                                        <AccordionWithList
                                            :is-loading="
                                                isLoading &&
                                                savingPart ===
                                                    'SurveyOpportunity'
                                            "
                                            @onSave="
                                                () =>
                                                    onSaveSurveyOpportunity(
                                                        surveyOpportunity
                                                    )
                                            "
                                            :isInitialOpen="
                                                currentOpportunityIndex ===
                                                surveyOpportunity?.id
                                            "
                                            :accordion="{
                                                name: surveyOpportunity
                                                    ?.opportunity?.name,
                                            }"
                                            leading
                                            @setOpen="
                                                (isOpen) =>
                                                    (surveyOpportunity.isExpanded =
                                                        isOpen)
                                            "
                                        >
                                            <div
                                                class="px-4 pt-4 space-y-10"
                                                :id="surveyOpportunity?.id"
                                                style="padding-bottom: 60px"
                                            >
                                                <div
                                                    v-for="question in surveyOpportunity?.surveyQuestionGroup?.questions?.sort(
                                                        (a, b) =>
                                                            a?.sortOrder >
                                                            b?.sortOrder
                                                                ? 1
                                                                : -1
                                                    )"
                                                    :key="question?.id"
                                                >
                                                    <Input
                                                        v-if="
                                                            question?.inputType ===
                                                                'TEXT' ||
                                                            question?.inputType ===
                                                                'NUMBER'
                                                        "
                                                        v-model:value="
                                                            question.question
                                                        "
                                                        :label="
                                                            question?.category
                                                        "
                                                    />
                                                    <Input
                                                        v-else-if="
                                                            question?.inputType ===
                                                                'TEXTAREA' ||
                                                            question?.inputType ===
                                                                'SLIDER' ||
                                                            question?.inputType ===
                                                                'BOOLEAN'
                                                        "
                                                        v-model:value="
                                                            question.question
                                                        "
                                                        :label="
                                                            question?.category
                                                        "
                                                    />
                                                    <div
                                                        v-else-if="
                                                            question?.inputType ===
                                                            'CHECKBOX'
                                                        "
                                                        class="space-y-10"
                                                    >
                                                        <Input
                                                            v-if="
                                                                question?.category?.includes(
                                                                    'Problems'
                                                                ) ||
                                                                question?.isShow ||
                                                                ((question?.category?.includes(
                                                                    'Solution'
                                                                ) ||
                                                                    question?.category?.includes(
                                                                        'Motivator'
                                                                    )) &&
                                                                    question
                                                                        ?.answerOptions
                                                                        ?.length >
                                                                        0)
                                                            "
                                                            v-model:value="
                                                                question.question
                                                            "
                                                            :label="
                                                                question?.category
                                                            "
                                                        />
                                                        <div
                                                            class="space-y-1 relative"
                                                        >
                                                            <Text
                                                                size="sm"
                                                                color="gray-700"
                                                                weight="semibold"
                                                                :content="
                                                                    question?.category?.replace(
                                                                        'question',
                                                                        'options'
                                                                    )
                                                                "
                                                            />
                                                            <div
                                                                class="divide-y divide-gray-300"
                                                            >
                                                                <div
                                                                    v-for="(
                                                                        answerOption,
                                                                        id
                                                                    ) in question.answerOptions"
                                                                    :key="
                                                                        answerOption?.id
                                                                    "
                                                                    class="px-3 py-2 space-x-4 flex justify-between items-center"
                                                                >
                                                                    <Text
                                                                        v-if="
                                                                            !answerOption?.isEdit
                                                                        "
                                                                        size="sm"
                                                                        weight="semibold"
                                                                        color="gray-600"
                                                                        :content="
                                                                            getAnswer(
                                                                                answerOption?.answer
                                                                            )
                                                                        "
                                                                    />
                                                                    <Input
                                                                        v-else
                                                                        :id="
                                                                            slugify(
                                                                                answerOption?.answer
                                                                            )
                                                                        "
                                                                        @keyup.enter="
                                                                            () =>
                                                                                (answerOption.isEdit = false)
                                                                        "
                                                                        @blur="
                                                                            () =>
                                                                                (answerOption.isEdit = false)
                                                                        "
                                                                        v-model:value="
                                                                            answerOption.answer
                                                                        "
                                                                        custom-class="text-black font-semibold text-sm"
                                                                    />
                                                                    <div
                                                                        class="flex items-center space-x-2"
                                                                    >
                                                                        <Text
                                                                            v-if="
                                                                                !answerOption?.isEdit &&
                                                                                answerOption
                                                                                    ?.metadata
                                                                                    ?.opportunityId &&
                                                                                answerOption
                                                                                    ?.metadata
                                                                                    ?.type !=
                                                                                    'PROBLEM'
                                                                            "
                                                                            @click="
                                                                                () =>
                                                                                    onEditAnswer(
                                                                                        answerOption
                                                                                    )
                                                                            "
                                                                            class="underline cursor-pointer"
                                                                            :content="'edit'"
                                                                            color="gray-500"
                                                                            hoverColor="gray-600"
                                                                            size="sm"
                                                                            weight="medium"
                                                                        />
                                                                        <Text
                                                                            v-if="
                                                                                answerOption
                                                                                    ?.metadata
                                                                                    ?.opportunityId ||
                                                                                answerOption
                                                                                    ?.metadata
                                                                                    ?.type ==
                                                                                    'PROBLEM'
                                                                            "
                                                                            @click="
                                                                                () => {
                                                                                    onDeleteAnswer(
                                                                                        question.answerOptions,
                                                                                        id,
                                                                                        surveyOpportunity,
                                                                                        question
                                                                                    );
                                                                                }
                                                                            "
                                                                            class="underline cursor-pointer"
                                                                            :content="'remove'"
                                                                            color="red-500"
                                                                            hoverColor="red-600"
                                                                            size="sm"
                                                                            weight="medium"
                                                                        />
                                                                        <Text
                                                                            v-if="
                                                                                !answerOption
                                                                                    ?.metadata
                                                                                    ?.opportunityId &&
                                                                                answerOption
                                                                                    ?.metadata
                                                                                    ?.temporaryOpportunityId
                                                                            "
                                                                            @click="
                                                                                () => {
                                                                                    onSetOpportunityIdToMetadata(
                                                                                        question.answerOptions,
                                                                                        id,
                                                                                        surveyOpportunity,
                                                                                        question
                                                                                    );
                                                                                }
                                                                            "
                                                                            class="underline cursor-pointer"
                                                                            :content="'approve'"
                                                                            color="indigo-500"
                                                                            hoverColor="indigo-600"
                                                                            size="sm"
                                                                            weight="medium"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Text
                                                                v-if="
                                                                    (question?.category?.includes(
                                                                        'Solution'
                                                                    ) ||
                                                                        question?.category?.includes(
                                                                            'Motivator'
                                                                        )) &&
                                                                    question
                                                                        ?.answerOptions
                                                                        ?.length ===
                                                                        0
                                                                "
                                                                size="sm"
                                                                :content="`This opportunity doesn't have any linked ${
                                                                    question?.category?.includes(
                                                                        'Solution'
                                                                    )
                                                                        ? 'solutions'
                                                                        : 'motivators'
                                                                }. Add one to ask your contacts how they ${surveyOpportunity?.opportunity?.name
                                                                    ?.split(' ')
                                                                    ?.slice(
                                                                        1,
                                                                        surveyOpportunity?.opportunity?.name?.split(
                                                                            ' '
                                                                        )
                                                                            ?.legnth
                                                                    )
                                                                    .join(' ')
                                                                    ?.toLowerCase()}.`"
                                                            />
                                                            <div
                                                                class="relative space-y-1"
                                                            >
                                                                <Text
                                                                    content="Link or Add New"
                                                                    color="primary-600"
                                                                    class="flex ml-4 sm:ml-0 underline cursor-pointer"
                                                                    hoverColor="primary-900"
                                                                    size="sm"
                                                                    weight="medium"
                                                                    @click="
                                                                        () => {
                                                                            question.isShow = true;
                                                                        }
                                                                    "
                                                                />
                                                                <div
                                                                    v-if="
                                                                        question.isShow
                                                                    "
                                                                    v-click-outside="
                                                                        () => {
                                                                            question.isShow = false;
                                                                        }
                                                                    "
                                                                >
                                                                    <OpportunityList
                                                                        :selectedOpportunities="
                                                                            question.selectedItems ||
                                                                            []
                                                                        "
                                                                        @onClickItem="
                                                                            (
                                                                                opportunity
                                                                            ) =>
                                                                                onSelectItems(
                                                                                    opportunity,
                                                                                    question
                                                                                )
                                                                        "
                                                                        :isNotAdd="
                                                                            question?.category?.includes(
                                                                                'Problem'
                                                                            )
                                                                        "
                                                                        :opportunities="
                                                                            getOpportunitiesList(
                                                                                question,
                                                                                surveyOpportunity
                                                                            )
                                                                        "
                                                                        @onAdd="
                                                                            (
                                                                                searchText
                                                                            ) => {
                                                                                if (
                                                                                    searchText?.length >
                                                                                    0
                                                                                ) {
                                                                                    question.answerOptions.push(
                                                                                        {
                                                                                            answer: question?.category?.includes(
                                                                                                'Problem'
                                                                                            )
                                                                                                ? upperFirst(
                                                                                                      searchText
                                                                                                  )
                                                                                                : searchText,
                                                                                        }
                                                                                    );
                                                                                }
                                                                                question.isShow = false;
                                                                                question.selectedItems =
                                                                                    [];
                                                                            }
                                                                        "
                                                                        :isLoading="
                                                                            isLoading &&
                                                                            savingPart ===
                                                                                'OPPORTUNITY'
                                                                        "
                                                                        :customClass="'left-0 top-5'"
                                                                        @onDone="
                                                                            () => {
                                                                                (
                                                                                    question.selectedItems ||
                                                                                    []
                                                                                ).map(
                                                                                    (
                                                                                        item
                                                                                    ) =>
                                                                                        question.answerOptions?.push(
                                                                                            {
                                                                                                answer: question?.category?.includes(
                                                                                                    'Problem'
                                                                                                )
                                                                                                    ? upperFirst(
                                                                                                          item.label
                                                                                                      )
                                                                                                    : item.label,
                                                                                                opportunityId:
                                                                                                    item?.id,
                                                                                            }
                                                                                        )
                                                                                );
                                                                                question.isShow = false;
                                                                            }
                                                                        "
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <template #heading>
                                                <div class="flex space-x-2">
                                                    <Text
                                                        @click="
                                                            () =>
                                                                onClickPreview(
                                                                    'SURVEY',
                                                                    surveyOpportunity,
                                                                    i
                                                                )
                                                        "
                                                        class="underline cursor-pointer"
                                                        :content="'preview'"
                                                        color="indigo-500"
                                                        hoverColor="indigo-600"
                                                        size="sm"
                                                        weight="medium"
                                                    />
                                                    <Text
                                                        @click="
                                                            () => {
                                                                selectedSurveyOpportunity =
                                                                    surveyOpportunity;
                                                                isOpenRemoveOpportunity = true;
                                                            }
                                                        "
                                                        class="underline cursor-pointer"
                                                        :content="'remove'"
                                                        color="red-500"
                                                        hoverColor="red-600"
                                                        size="sm"
                                                        weight="medium"
                                                    />
                                                </div>
                                            </template>
                                        </AccordionWithList>
                                    </template>
                                </draggable>
                            </div>
                            <AccordionWithList
                                hide-bottom
                                :accordion="{ name: 'Demographic Questions' }"
                                leading
                            >
                                <div class="p-4 space-y-2">
                                    <Text
                                        size="xs"
                                        color="black"
                                        custom-class="italic"
                                        element="span"
                                        v-html="
                                            `At the end of each survey we ask some basic demographic questions about each person so you can see how representative your responses are.<br>For example, if your Target Customer is all adults but everyone who responds is under 30, your data biased and you should either update your definition or get more data for a representative group. <b>These questions are not editable.</b></b>`
                                        "
                                    />
                                    <div />
                                    <div
                                        v-for="tag in getDemographicQuestions()"
                                        :key="tag.id"
                                        class=""
                                    >
                                        <Text
                                            size="xs"
                                            color="black"
                                            weight="semibold"
                                            :content="tag.label"
                                        />
                                    </div>
                                </div>
                                <template #heading>
                                    <Text
                                        @click="
                                            () =>
                                                onClickPreview(
                                                    'DEMOGRAPHIC',
                                                    surveyOpportunity
                                                )
                                        "
                                        class="underline cursor-pointer"
                                        :content="'preview'"
                                        color="indigo-500"
                                        hoverColor="indigo-600"
                                        size="sm"
                                        weight="medium"
                                    />
                                </template>
                            </AccordionWithList>
                            <AccordionWithList
                                :is-loading="
                                    isLoading && savingPart === 'Survey'
                                "
                                @onSave="onSaveSurvey"
                                :accordion="{ name: 'Conclusion' }"
                                leading
                            >
                                <div class="p-4 space-y-2">
                                    <Input
                                        label="Heading"
                                        v-model:value="survey.closingHeading"
                                    />
                                    <Input
                                        label="Description"
                                        v-model:value="
                                            survey.closingDescription
                                        "
                                    />
                                </div>
                                <template #heading>
                                    <Text
                                        @click="
                                            () =>
                                                onClickPreview(
                                                    'CONCLUSION',
                                                    survey
                                                )
                                        "
                                        class="underline cursor-pointer"
                                        :content="'preview'"
                                        color="indigo-500"
                                        hoverColor="indigo-600"
                                        size="sm"
                                        weight="medium"
                                    />
                                </template>
                            </AccordionWithList>
                        </div>
                    </div>
                </div>
            </div>

            <DeleteModal
                :is-show="isOpenRemoveSurvey"
                @onClose="() => (isOpenRemoveSurvey = false)"
                :title="`Archive this <b>Survey</b>?`"
                :description="`Are you sure you want to archive the <b>${survey?.name}</b>? They will remove all responses and associated data.`"
                yes-btn-text="Archive"
                no-btn-text="Cancel"
                icon="TrashIcon"
                yesBtnVariant="danger"
                noBtnVariant="secondary"
                :on-no-click="() => (isOpenRemoveSurvey = false)"
                :on-yes-click="() => onRemoveSurvey()"
            />
            <DeleteModal
                :isShow="isOpenRemove"
                @onClose="() => (isOpenRemove = false)"
                :title="`Delete <b>${targetCustomerUser?.name || ''}</b>?`"
                :description="`Are you sure you want to delete this <b>Target Customer</b>? They will delete all the data linked to it, including opportunities, as well.`"
                yes-btn-text="Archive"
                no-btn-text="Cancel"
                icon="TrashIcon"
                yesBtnVariant="danger"
                noBtnVariant="secondary"
                :on-no-click="() => (isOpenRemove = false)"
                :on-yes-click="() => onRemoveTargetCustomer()"
            />
            <DeleteModal
                :isShow="isOpenRemoveOpportunity"
                @onClose="() => (isOpenRemoveOpportunity = false)"
                :title="`Remove this <b>Opportunity</b>?`"
                :description="`Are you sure you want to remove <b>${selectedSurveyOpportunity?.opportunity?.name}</b> from <b>${survey?.name}</b>?`"
                yes-btn-text="Remove"
                no-btn-text="Cancel"
                icon="TrashIcon"
                yesBtnVariant="danger"
                noBtnVariant="secondary"
                :on-no-click="() => (isOpenRemoveOpportunity = false)"
                :on-yes-click="() => onRemoveSurveyOpportunity()"
            />
            <WithNoSwiper
                v-bind="{
                    isShow: isShow,
                    isLoading: false,
                    isSaving: isLoading,
                    list: contactForm,
                    modelKey: 'modelField',
                    title: 'Add a new Contact',
                    formData: formData,
                    content:
                        'Contacts are real people that fit your target customer definition.',
                }"
                :saveForm="saveForm"
                @onClose="
                    () => {
                        isShow = false;
                    }
                "
            />
            <WithNoSwiper
                v-bind="{
                    isShow: isShowOpportunity,
                    isSaving: isSaving,
                    list: opportunityForm,
                    modelKey: 'modelField',
                    title: 'Add an Opportunity',
                    formData: formData,
                    isHelp: true,
                    content:
                        'Opportunities are things that people do and sometimes have problems doing.',
                }"
                :saveForm="saveOpportunityForm"
                @onClose="
                    () => {
                        isShowOpportunity = false;
                    }
                "
            >
            </WithNoSwiper>
            <ImportModal
                v-bind="{
                    isShow: isShowImport,
                    showCustomContent: true,
                    isLoading: false,
                    isSaving: isLoading,
                    title: 'Import Contacts',
                }"
                @onClose="
                    () => {
                        isShowImport = false;
                    }
                "
                :targetCustomerUsers="
                    this?.venture?.targetCustomerUsers?.filter(
                        (t) => !t?.isArchived
                    ) || []
                "
                :targetCustomerUser="this.targetCustomerUser"
                @saveForm="saveForm"
            />

            <!-- Opportunity Map Modal -->
            <div
                v-if="isShowOpportunityMap"
                class="fixed inset-0 bg-white z-50 flex flex-col"
            >
                <!-- Header with close button -->
                <div
                    class="flex justify-between items-center p-4 border-b border-gray-200 bg-white"
                >
                    <div class="flex items-center space-x-4">
                        <h2 class="text-2xl font-bold text-gray-900">
                            Opportunity Map
                        </h2>
                        <!-- Share Report Link -->
                        <a @click="shareOpportunityMap" class="cursor-pointer">
                            <span
                                class="inline-flex items-center underline text-sm text-primary-900"
                            >
                                <Icons
                                    name="UploadIcon"
                                    size="4"
                                    color="primary-900"
                                    customClass="mr-2"
                                    hover-text-color="primary-900"
                                />
                                Share Report
                            </span>
                        </a>
                    </div>
                    <div class="flex items-center space-x-3">
                        <!-- More Details Button -->
                        <Button
                            @click="showMoreDetails"
                            content="More Details"
                            variant="secondary"
                            size="sm"
                            class="h-8"
                        />

                        <!-- Close Button -->
                        <button
                            @click="isShowOpportunityMap = false"
                            class="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <!-- Full page content -->
                <div class="flex-1 overflow-hidden">
                    <div class="h-full overflow-y-auto bg-gray-100">
                        <div class="opportunity-map-modal h-full">
                            <div class="h-full bg-white">
                                <OpportunityMap
                                    :nodes="opportunityMapData.nodes"
                                    :links="opportunityMapData.links"
                                    :legend="opportunityMapData.legend"
                                    :edgeClicked="() => {}"
                                    :nodeMapping="
                                        opportunityMapData.nodeMapping
                                    "
                                    :nodeClicked="() => {}"
                                    :badgeText="opportunityMapData.badgeText"
                                    :title="
                                        opportunityMapData.detailDialogTitle
                                    "
                                    :demographics="
                                        opportunityMapData.demographics
                                    "
                                    :navigationGraph="
                                        opportunityMapData.navigationGraph
                                    "
                                    :dialogTitle="
                                        opportunityMapData.dialogTitle
                                    "
                                    :label="opportunityMapData.label"
                                    :tables="opportunityMapData.tables"
                                />
                            </div>
                        </div>

                        <!-- Research Process Notification Popover -->
                        <div
                            v-if="showResearchNotification"
                            class="fixed bottom-6 right-6 z-50 max-w-sm"
                        >
                            <div
                                class="bg-white rounded-lg shadow-lg border border-gray-200 p-4"
                            >
                                <!-- Close button -->
                                <button
                                    @click="showResearchNotification = false"
                                    class="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                                >
                                    <Icons name="XMarkIcon" size="4" />
                                </button>

                                <!-- Header -->
                                <div class="mb-3 pr-6">
                                    <h3
                                        class="text-lg font-semibold text-gray-900"
                                    >
                                        Your next step is to start the research
                                        process for adults in physical therapy
                                    </h3>
                                </div>

                                <!-- Body -->
                                <div class="mb-4">
                                    <p class="text-sm text-gray-600">
                                        Your next step is to start the research
                                        process for adults in physical therapy
                                    </p>
                                </div>

                                <!-- Footer -->
                                <div class="flex justify-end">
                                    <Button
                                        @click="startResearch"
                                        content="Start your research"
                                        variant="primary"
                                        size="sm"
                                        class="h-8"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Research Flow Modal -->
            <div
                v-if="isShowResearchFlow"
                class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
                <div
                    class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                >
                    <!-- Header with close button -->
                    <div
                        class="flex justify-between items-center p-6 border-b border-gray-200"
                    >
                        <div></div>
                        <button
                            @click="isShowResearchFlow = false"
                            class="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                        >
                            ✕
                        </button>
                    </div>

                    <!-- Progress Steps -->
                    <div class="p-6">
                        <div class="flex items-center justify-center mb-8">
                            <!-- Step 1: Research Path -->
                            <div
                                class="flex items-center cursor-pointer"
                                @click="researchFlowStep = 1"
                            >
                                <div
                                    class="flex items-center justify-center w-8 h-8 rounded-full bg-gray-500 text-white text-sm font-semibold"
                                >
                                    ✓
                                </div>
                                <span
                                    class="ml-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                                    >Research Path</span
                                >
                            </div>

                            <!-- Arrow to step 2 -->
                            <div class="flex-1 h-0.5 bg-gray-300 mx-4"></div>

                            <!-- Step 2: Build Contact List -->
                            <div
                                class="flex items-center cursor-pointer"
                                @click="researchFlowStep = 2"
                            >
                                <div
                                    :class="
                                        researchFlowStep >= 2
                                            ? 'bg-gray-500 text-white'
                                            : 'border-2 border-gray-500 text-gray-500'
                                    "
                                    class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                                >
                                    2
                                </div>
                                <span
                                    :class="
                                        researchFlowStep >= 2
                                            ? 'text-gray-700 hover:text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700'
                                    "
                                    class="ml-2 text-sm font-medium"
                                    >Build Contact List</span
                                >
                            </div>

                            <!-- Arrow to step 3 -->
                            <div class="flex-1 h-0.5 bg-gray-300 mx-4"></div>

                            <!-- Step 3: Outreach -->
                            <div
                                class="flex items-center cursor-pointer"
                                @click="researchFlowStep = 3"
                            >
                                <div
                                    :class="
                                        researchFlowStep >= 3
                                            ? 'bg-gray-500 text-white'
                                            : 'border-2 border-gray-300 text-gray-500'
                                    "
                                    class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
                                >
                                    3
                                </div>
                                <span
                                    :class="
                                        researchFlowStep >= 3
                                            ? 'text-gray-700 hover:text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700'
                                    "
                                    class="ml-2 text-sm font-medium"
                                    >Outreach</span
                                >
                            </div>
                        </div>

                        <!-- Step Content -->
                        <div class="text-left">
                            <!-- Step 1: Research Path Selection -->
                            <div v-if="researchFlowStep === 1">
                                <h3
                                    class="text-xl font-semibold text-gray-900 mb-4"
                                >
                                    How would you like to do your research?
                                </h3>
                                <p class="text-gray-600 mb-8 max-w-2xl">
                                    Your first step is to choose whether you'd
                                    like to interview your customers to identify
                                    new opportunities and build empathy and
                                    survey them later, or skip right to
                                    measuring opportunities with surveys. You
                                    can always change your mind.
                                </p>

                                <!-- Research Options -->
                                <div class="space-y-6">
                                    <!-- Option 1: Interviews + Surveys -->
                                    <div
                                        class="border border-gray-200 rounded-lg p-6 text-left"
                                    >
                                        <h4
                                            class="text-lg font-semibold text-gray-900 mb-3"
                                        >
                                            Interviews + Surveys
                                        </h4>
                                        <p class="text-gray-600 mb-4">
                                            We'll help you build a contact list,
                                            reach out to request interviews,
                                            conduct those interviews and
                                            identify new insights, then help you
                                            build and send your survey to
                                            measure the viability of different
                                            opportunities.
                                        </p>
                                        <div
                                            v-if="!hasExistingResearch"
                                            class="flex justify-end"
                                        >
                                            <Button
                                                @click="
                                                    selectResearchPath(
                                                        'interviews'
                                                    )
                                                "
                                                content="Get Started"
                                                variant="primary"
                                                size="md"
                                            />
                                        </div>
                                        <div v-else class="flex justify-end">
                                            <span
                                                class="text-sm text-green-600 font-medium"
                                            >
                                                ✓ Research already configured
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Option 2: Surveys Only -->
                                    <div
                                        class="border border-gray-200 rounded-lg p-6 text-left"
                                    >
                                        <h4
                                            class="text-lg font-semibold text-gray-900 mb-3"
                                        >
                                            Surveys Only
                                        </h4>
                                        <p class="text-gray-600 mb-4">
                                            We'll help you build and send your
                                            survey to measure the viability of
                                            different opportunities as quickly
                                            as possible.
                                        </p>
                                        <div
                                            v-if="!hasExistingResearch"
                                            class="flex justify-end"
                                        >
                                            <Button
                                                @click="
                                                    selectResearchPath(
                                                        'surveys'
                                                    )
                                                "
                                                content="Get Started"
                                                variant="primary"
                                                size="md"
                                            />
                                        </div>
                                        <div v-else class="flex justify-end">
                                            <span
                                                class="text-sm text-green-600 font-medium"
                                            >
                                                ✓ Research already configured
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Step 2: Build Contact List -->
                            <div v-if="researchFlowStep === 2">
                                <h3
                                    class="text-xl font-semibold text-gray-900 mb-4"
                                >
                                    Let's build your contact list!
                                </h3>
                                <p class="text-gray-600 mb-8 max-w-2xl">
                                    Below is a list of your target customer
                                    groups and the places you can find their
                                    contact info. We can do it for you
                                    automatically (some you might have to
                                    authenticate), or you can do it yourself.
                                </p>

                                <!-- Target Customer Group -->
                                <div class="mb-6">
                                    <h4
                                        class="text-lg font-semibold text-gray-900 mb-4"
                                    >
                                        Adults in physical therapy
                                    </h4>

                                    <!-- Contact Sources -->
                                    <div class="space-y-4">
                                        <!-- Dynamic Research Areas Grouped by Platform -->
                                        <div
                                            v-for="(
                                                platformGroup, platform
                                            ) in groupedOutreachAreas"
                                            :key="platform"
                                            class="flex items-center justify-between border border-gray-200 rounded-lg p-4"
                                        >
                                            <div class="flex items-center">
                                                <span
                                                    class="font-medium text-gray-900 mr-4"
                                                    >{{ platform }}</span
                                                >
                                                <span
                                                    v-if="
                                                        platformGroup.some(
                                                            (area) =>
                                                                area.needsAuth
                                                        )
                                                    "
                                                    class="text-sm text-gray-500"
                                                    >(needs
                                                    authentication)</span
                                                >
                                            </div>
                                            <div
                                                class="flex items-center space-x-3"
                                            >
                                                <span
                                                    v-if="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ]
                                                    "
                                                    class="text-sm text-gray-600"
                                                >
                                                    {{
                                                        contactCounts[
                                                            platform
                                                        ] || 0
                                                    }}
                                                    contacts found
                                                </span>
                                                <a
                                                    v-if="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ] &&
                                                        loadingStates[platform]
                                                    "
                                                    @click="
                                                        (event) => {
                                                            event.preventDefault();
                                                            cancelContactSearch(
                                                                platform
                                                            );
                                                        }
                                                    "
                                                    href="#"
                                                    class="text-red-600 underline text-sm cursor-pointer"
                                                    >Cancel</a
                                                >
                                                <a
                                                    v-if="
                                                        !hasStartedContactSearch[
                                                            platform
                                                        ]
                                                    "
                                                    :href="
                                                        platformGroup[0]
                                                            ?.link || '#'
                                                    "
                                                    class="text-primary-900 underline text-sm"
                                                    >Do it yourself</a
                                                >
                                                <Button
                                                    v-if="
                                                        !allPlatformsHaveContacts
                                                    "
                                                    :content="
                                                        hasStartedContactSearch[
                                                            platform
                                                        ] &&
                                                        !loadingStates[
                                                            platform
                                                        ] &&
                                                        (contactCounts[
                                                            platform
                                                        ] || 0) > 0
                                                            ? 'Reach out'
                                                            : platformGroup.some(
                                                                  (area) =>
                                                                      area.needsAuth
                                                              )
                                                            ? 'Authorize'
                                                            : 'Find Contacts'
                                                    "
                                                    variant="primary"
                                                    size="sm"
                                                    class="h-8"
                                                    :isLoading="
                                                        loadingStates[platform]
                                                    "
                                                    @click="
                                                        findContacts(
                                                            platform,
                                                            platformGroup
                                                        )
                                                    "
                                                />
                                                <span
                                                    v-else
                                                    class="text-sm text-green-600 font-medium"
                                                >
                                                    ✓ Contacts found
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Fallback if no research areas -->
                                        <div
                                            v-if="
                                                !targetCustomerUser?.outreachAreas ||
                                                targetCustomerUser.outreachAreas
                                                    .length === 0
                                            "
                                            class="text-gray-500 text-center py-8"
                                        >
                                            No research areas configured yet.
                                        </div>
                                    </div>
                                </div>

                                <!-- Continue Button -->
                                <div class="flex justify-end mt-8">
                                    <Button
                                        @click="researchFlowStep = 3"
                                        content="Continue"
                                        variant="primary"
                                        size="lg"
                                    />
                                </div>
                            </div>

                            <!-- Step 3: Setup Outreach Plan -->
                            <div v-if="researchFlowStep === 3">
                                <!-- Show contacts if budget is already set -->
                                <div v-if="isBudgetSet">
                                    <h3
                                        class="text-xl font-semibold text-gray-900 mb-4"
                                    >
                                        Your Contact List
                                    </h3>
                                    <p class="text-gray-600 mb-8 max-w-2xl">
                                        Here are the contacts we found for your
                                        target customers, grouped by platform.
                                    </p>

                                    <!-- Contacts grouped by platform -->
                                    <div class="space-y-6">
                                        <div
                                            v-for="(
                                                contacts, platform
                                            ) in groupedContacts"
                                            :key="platform"
                                            class="border border-gray-200 rounded-lg p-6"
                                        >
                                            <h4
                                                class="text-lg font-semibold text-gray-900 mb-4"
                                            >
                                                {{ platform }} ({{
                                                    contacts.length
                                                }}
                                                contacts)
                                            </h4>
                                            <div
                                                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                                            >
                                                <div
                                                    v-for="contact in contacts"
                                                    :key="contact.id"
                                                    class="border border-gray-100 rounded-lg p-4"
                                                >
                                                    <div
                                                        class="font-medium text-gray-900"
                                                    >
                                                        {{ contact.firstName }}
                                                        {{ contact.lastName }}
                                                    </div>
                                                    <div
                                                        class="text-sm text-gray-600 mt-1"
                                                    >
                                                        {{ contact.email }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            contact.phones
                                                                ?.length
                                                        "
                                                        class="text-sm text-gray-600"
                                                    >
                                                        {{
                                                            contact.phones[0]
                                                                .number
                                                        }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            contact
                                                                .socialHandles
                                                                ?.length
                                                        "
                                                        class="text-sm text-blue-600 mt-1"
                                                    >
                                                        @{{
                                                            contact
                                                                .socialHandles[0]
                                                                .handle
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Show budget inputs if budget is not set -->
                                <div v-else>
                                    <h3
                                        class="text-xl font-semibold text-gray-900 mb-4"
                                    >
                                        Let's setup your outreach plan
                                    </h3>
                                    <p class="text-gray-600 mb-8 max-w-2xl">
                                        Set your budget for interviews and
                                        surveys to get started with outreach.
                                    </p>

                                    <!-- Interviews Section -->
                                    <div class="mb-8">
                                        <h4
                                            class="text-lg font-semibold text-gray-900 mb-4"
                                        >
                                            Interviews (at least 12)
                                        </h4>
                                        <div
                                            class="flex items-center space-x-4 mb-4"
                                        >
                                            <div
                                                class="flex items-center space-x-2"
                                            >
                                                <input
                                                    v-model="interviewReward"
                                                    type="text"
                                                    class="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    placeholder="$20"
                                                />
                                                <span class="text-gray-500"
                                                    >x</span
                                                >
                                                <input
                                                    v-model="
                                                        maxInterviewRewards
                                                    "
                                                    type="text"
                                                    class="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    placeholder="15"
                                                />
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                = ${{ interviewBudget }}
                                                Interview Budget
                                            </div>
                                        </div>
                                        <p class="text-sm text-gray-600">
                                            I'm offering ${{
                                                interviewReward || "20"
                                            }}
                                            to the first
                                            {{ maxInterviewRewards || "15" }}
                                            people that are willing to talk.
                                        </p>
                                    </div>

                                    <!-- Surveys Section -->
                                    <div class="mb-8">
                                        <h4
                                            class="text-lg font-semibold text-gray-900 mb-4"
                                        >
                                            Surveys (at least 30)
                                        </h4>
                                        <div
                                            class="flex items-center space-x-4 mb-4"
                                        >
                                            <div
                                                class="flex items-center space-x-2"
                                            >
                                                <input
                                                    v-model="surveyReward"
                                                    type="text"
                                                    class="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    placeholder="$0"
                                                />
                                                <span class="text-gray-500"
                                                    >x</span
                                                >
                                                <input
                                                    v-model="maxSurveyRewards"
                                                    type="text"
                                                    class="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                = ${{ surveyBudget }} Survey
                                                Budget
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Action Button -->
                                    <div class="flex justify-end">
                                        <Button
                                            @click="saveOutreachPlan"
                                            content="Continue"
                                            variant="primary"
                                            size="lg"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Survey Flow Modal -->
            <div
                v-if="isShowSurveyFlow"
                class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
                <div
                    class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                >
                    <!-- Modal Header -->
                    <div
                        class="flex items-center justify-between p-6 border-b border-gray-200"
                    >
                        <div class="flex items-center space-x-4">
                            <!-- Progress Steps -->
                            <div class="flex items-center space-x-2">
                                <div class="flex items-center">
                                    <div
                                        class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
                                    >
                                        <svg
                                            class="w-4 h-4 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </div>
                                    <span
                                        class="ml-2 text-sm font-medium text-gray-700"
                                        >Conduct interviews</span
                                    >
                                </div>
                                <div class="w-8 h-0.5 bg-gray-300"></div>
                                <div class="flex items-center">
                                    <div
                                        class="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center"
                                    >
                                        <span
                                            class="text-white text-sm font-medium"
                                            >{{ surveyFlowStep }}</span
                                        >
                                    </div>
                                    <span
                                        class="ml-2 text-sm font-medium text-gray-700"
                                        >Build your survey</span
                                    >
                                </div>
                                <div class="w-8 h-0.5 bg-gray-300"></div>
                                <div class="flex items-center">
                                    <div
                                        class="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center"
                                    >
                                        <span
                                            class="text-gray-400 text-sm font-medium"
                                            >3</span
                                        >
                                    </div>
                                    <span
                                        class="ml-2 text-sm font-medium text-gray-400"
                                        >Send your survey</span
                                    >
                                </div>
                            </div>
                        </div>
                        <button
                            @click="isShowSurveyFlow = false"
                            class="text-gray-400 hover:text-gray-600"
                        >
                            <svg
                                class="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                ></path>
                            </svg>
                        </button>
                    </div>

                    <!-- Modal Content -->
                    <div class="p-6">
                        <h2 class="text-2xl font-bold text-gray-900 mb-4">
                            It's time to build your survey
                        </h2>
                        <p class="text-gray-600 mb-6">
                            Your survey is the final step of measuring how
                            viable each opportunity is. Once you select up to 3
                            opportunities to add to your survey, we will create
                            all of the questions for you to review before
                            sending it to your contacts.
                        </p>

                        <!-- Step 1: Select Opportunities -->
                        <div v-if="surveyFlowStep === 1">
                            <h3
                                class="text-lg font-semibold text-gray-900 mb-4"
                            >
                                Select your opportunities ({{
                                    selectedSurveyOpportunities.length
                                }})
                            </h3>

                            <!-- Selected Opportunities -->
                            <div class="space-y-2 mb-4">
                                <div
                                    v-if="
                                        selectedSurveyOpportunities.length === 0
                                    "
                                    class="p-3 bg-gray-100 rounded-lg text-gray-500 text-sm"
                                >
                                    No opportunities selected yet. Click "Select
                                    Opportunities" below to choose up to 3
                                    opportunities for your survey.
                                </div>
                                <div
                                    v-for="opportunity in selectedSurveyOpportunities"
                                    :key="opportunity.id"
                                    class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                >
                                    <span class="text-gray-900">{{
                                        opportunity.name
                                    }}</span>
                                    <button
                                        @click="
                                            removeSurveyOpportunity(
                                                opportunity.id
                                            )
                                        "
                                        class="text-red-600 underline text-sm"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>

                            <!-- Select Opportunities Button -->
                            <div class="relative pb-4">
                                <Text
                                    content="Select Opportunities"
                                    color="primary-600"
                                    class="flex underline cursor-pointer"
                                    hoverColor="primary-900"
                                    size="sm"
                                    weight="medium"
                                    @click="
                                        () => {
                                            isShowOpportunitySelect = true;
                                        }
                                    "
                                />
                                <div
                                    v-if="isShowOpportunitySelect"
                                    v-click-outside="
                                        () => {
                                            // Only close if we're not in survey flow or if user clicks outside
                                            if (!isShowSurveyFlow) {
                                                isShowOpportunitySelect = false;
                                                selectedOpportunities = [];
                                            }
                                        }
                                    "
                                >
                                    <OpportunityList
                                        :selectedOpportunities="
                                            selectedOpportunities
                                        "
                                        @onClickItem="
                                            (opportunity) => {
                                                onSelectOpportunity(
                                                    opportunity
                                                );
                                            }
                                        "
                                        :opportunities="
                                            targetCustomerUser?.opportunities ||
                                            []
                                        "
                                        @onAdd="
                                            (searchText) => {
                                                if (!isShowSurveyFlow) {
                                                    onAddOpportunities({
                                                        searchText,
                                                    });
                                                }
                                            }
                                        "
                                        :isLoading="
                                            isLoading &&
                                            savingPart === 'OPPORTUNITY'
                                        "
                                        :customClass="'left-0 top-6'"
                                        @onDone="
                                            () => {
                                                isShowOpportunitySelect = false;
                                            }
                                        "
                                    />
                                </div>

                                <!-- Survey Rewards Section -->
                                <div class="border-t border-gray-200 pt-6">
                                    <h3
                                        class="text-lg font-semibold text-gray-900 mb-4"
                                    >
                                        Survey rewards (optional)
                                    </h3>
                                    <div class="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label
                                                class="block text-sm font-medium text-gray-700 mb-1"
                                                >Survey Reward</label
                                            >
                                            <div class="relative">
                                                <div
                                                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                                >
                                                    <span
                                                        class="text-gray-500 sm:text-sm"
                                                        >$</span
                                                    >
                                                </div>
                                                <input
                                                    v-model="surveyRewardAmount"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0"
                                                    class="pl-7 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                class="block text-sm font-medium text-gray-700 mb-1"
                                                >Max Survey Rewards</label
                                            >
                                            <input
                                                v-model="maxSurveyRewardsAmount"
                                                type="number"
                                                min="30"
                                                placeholder="30"
                                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <p
                                                class="text-xs text-gray-500 mt-1"
                                            >
                                                Minimum: 30
                                            </p>
                                        </div>
                                    </div>
                                    <div class="bg-gray-50 p-3 rounded-md">
                                        <span
                                            class="text-sm font-medium text-gray-900"
                                            >= ${{ surveyBudgetAmount }} Survey
                                            Budget</span
                                        >
                                    </div>
                                </div>
                            </div>

                            <!-- Step 2: Build Survey (Placeholder) -->
                            <div v-if="surveyFlowStep === 2">
                                <h3
                                    class="text-lg font-semibold text-gray-900 mb-4"
                                >
                                    Build your survey
                                </h3>
                                <p class="text-gray-600">
                                    Survey building interface will be
                                    implemented here...
                                </p>
                            </div>

                            <!-- Step 3: Send Survey (Placeholder) -->
                            <div v-if="surveyFlowStep === 3">
                                <h3
                                    class="text-lg font-semibold text-gray-900 mb-4"
                                >
                                    Send your survey
                                </h3>
                                <p class="text-gray-600">
                                    Survey sending interface will be implemented
                                    here...
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Footer -->
                    <div class="flex justify-end p-6 border-t border-gray-200">
                        <button
                            v-if="surveyFlowStep === 1"
                            @click="createSurvey"
                            :disabled="
                                selectedSurveyOpportunities.length !== 3 ||
                                (isLoading && savingPart === 'SURVEY')
                            "
                            class="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            {{
                                isLoading && savingPart === "SURVEY"
                                    ? "Creating Survey..."
                                    : "Create Survey"
                            }}
                        </button>
                        <button
                            v-if="surveyFlowStep > 1"
                            @click="surveyFlowStep++"
                            class="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.opportunity-map-modal :deep(.overflow-y-scroll) {
    height: 100% !important;
    padding-top: 0 !important;
}

.opportunity-map-modal :deep(.h-screen) {
    height: 100% !important;
}

.opportunity-map-modal :deep(.pt-16) {
    padding-top: 0 !important;
}
</style>

<script>
import ListViewItem from "@/components/organisms/ListViewItem/ListViewItem";
import DetailViewHeader from "@/components/molecules/DetailViewHeader/DetailViewHeader.vue";
import SubNavigationBar from "@/components/molecules/SubnavigationBar/SubnavigationBar.vue";
import FormFieldGroup from "@/components/organisms/FormFieldGroup/FormFieldGroup";
import { c } from "@/components/constants";
import Text from "@/components/atoms/Text/Text";
import Icon from "@/components/atoms/Icons/Icons";
import Input from "@/components/molecules/Inputs/Component/Component";
import Select from "@/components/molecules/Selects/Component/Component";
import Button from "@/components/atoms/Button/Button";
import Textarea from "@/components/molecules/Textareas/Simple/Simple";
import Badge from "@/components/molecules/Badge/Basic/Basic";
import Items from "@/components/molecules/CardListItem/CardWithSurveyResult/CardWithSurveyResult";
import ListViewHeader from "@/components/molecules/ListViewHeader/WithTitleAndButton/WithTitleAndButton";
import HeaderWithFilter from "@/components/molecules/ListViewHeader/WithFilter/ListViewHeader";
import Table from "@/components/organisms/Tables/Simple/Simple";
import SortTable from "@/components/organisms/Tables/WithSortAndFilter/WithSortAndFilter.vue";
import Link from "@/components/atoms/Link/Link";
import AccordionWithList from "@/components/molecules/Accordions/AccordionWithList/AccordionWithList";
import DemographicDataSummary from "@/components/organisms/DemographicDataSummary/DemographicDataSummary";
import EmptyState from "@/components/organisms/EmptyState/Simple/Simple.vue";
import TableWithInput from "@/components/organisms/Tables/WithInput/WithInput";
import { Settings } from "../../settings";
import { upperFirst } from "lodash";
import moment from "moment";
import vClickOutside from "click-outside-vue3";
import DeleteModal from "@/components/organisms/Modals/SimpleAlert/SimpleAlert";
import { contactForm, opportunityForm } from "../../.storybook/sampleData";
import WithNoSwiper from "@/components/organisms/Modals/WithNoSwiper/WithNoSwiper";
import { convertEnumToRead, convertActionWord } from "@/utils/utils.js";
import { Editor, EditorContent } from "@tiptap/vue-3";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import StarterKit from "@tiptap/starter-kit";
import { json } from "overmind";
import AutoComplete from "@/components/molecules/AutoComplete/AutoComplete";
import InputWithLeadingIcon from "@/components/molecules/Inputs/InputWithLeadingIcon/InputWithLeadingIcon.vue";
import OpportunityList from "@/components/organisms/Modals/OpportunityList/OpportunityList";
import useClipboard from "vue-clipboard3";
import draggable from "vuedraggable";
import slugify from "slugify";
import ImportModal from "@/components/organisms/Modals/ImportModal/ImportModal";
import OpportunityMapView from "@/components/templates/OpportunityMapView/OpportunityMapView";
import Loader from "../components/atoms/Loader/Loader.vue";
import { opportunity, opportunityLinks } from "../../.storybook/sampleData";
import OpportunityMap from "@/components/organisms/OpportunityMap/OpportunityMap.vue";
import Header from "@/components/organisms/Headers/WithButtonAndLabel/WithButtonAndLabel.vue";

export default {
    components: {
        DemographicDataSummary,
        AccordionWithList,
        ListViewItem,
        DetailViewHeader,
        SubNavigationBar,
        FormFieldGroup,
        Text,
        Icon,
        Input,
        Select,
        Button,
        Textarea,
        Badge,
        Items,
        ListViewHeader,
        Table,
        SortTable,
        Link,
        EmptyState,
        TableWithInput,
        DeleteModal,
        WithNoSwiper,
        EditorContent,
        HeaderWithFilter,
        AutoComplete,
        InputWithLeadingIcon,
        OpportunityList,
        draggable,
        ImportModal,
        OpportunityMapView,
        Loader,
        OpportunityMap,
        Header,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    data() {
        return {
            detailViewHeader: c.targetCustomerHeader,
            formFieldGroup: c.formFieldGroup,
            targetCustomer: null,
            targetCustomerUser: null,
            research: null,
            formData: {},
            tabs: [],
            selectedTab: null,
            selectedNav: null,
            opportunities: [],
            contacts: [],
            contactForm,
            formData: {},
            interviews: [],
            activeIndex: null,
            filters: c.ventureOpportunities.listHeader.filter,
            sorts: c.ventureOpportunities.listHeader.sort,
            filter: null,
            sort: null,
            searchText: null,
            headers: [],
            userSurveys: [],
            links: [
                {
                    content: "Import Contacts",
                    decoration: "underline",
                    action: () => {
                        this.isShowImport = true;
                    },
                },
                {
                    content: "Export as CSV",
                    decoration: "underline",
                },
                {
                    content: "View Interview Schedule",
                    decoration: "underline",
                },
            ],
            introAccordion: { name: "Introduction" },
            optionAccordion: { name: "Optional Target Questions" },
            demographics: [],
            isLoading: false,
            savingPart: null,
            emptyViewBinding: {
                title: "Let's create your survey!",
                description:
                    "All you have to do is pick your Opportunities and make sure the questions sound natural.",
                btnText: "Create Survey",
                icon: "LightBulbIcon",
            },
            emptyOpportunity: {
                title: "Let's pick the Opportunities to measure",
                description:
                    "Select up to 3 Opportunities you'd like to measure and compare.",
                btnText: "Select Opportunities",
                icon: "LightBulbIcon",
            },
            emptyContact: {
                title: "Let's add the contacts",
                description: "Please add the contacts",
                btnText: "Add Contact",
                icon: "LightBulbIcon",
            },
            survey: null,
            upperFirst,
            opItems: [],
            selectedOpportunities: [],
            isShowOpportunitySelect: false,
            completedSurveys: [],
            isShowList: false,
            isEditSurveyName: false,
            isEditScriptName: false,
            isOpenRemoveSurvey: false,
            isOpenRemove: false,
            isShow: false,
            isShowOpportunity: false,
            surveyData: [],
            opportunityForm,
            isSaving: false,
            editor: null,
            convertEnumToRead,
            moment,
            Settings,
            selectedSurveyOpportunity: null,
            isOpenRemoveOpportunity: false,
            currentOpportunityIndex: null,
            c,
            motivators: [],
            solutions: [],
            problems: [],
            slugify,
            isShowInterviewList: false,
            selectedInterviewScript: null,
            isAdding: false,
            isShowImport: false,
            isShowOpportunityMap: false,
            isShowSurveyFlow: false,
            surveyFlowStep: 1,
            selectedSurveyOpportunities: [],
            surveyRewardAmount: "0",
            maxSurveyRewardsAmount: "30",
            loadingStates: {}, // Track loading state for each platform
            contactCounts: {}, // Track contact counts for each platform
            hasStartedContactSearch: {}, // Track if contact search has been initiated for each platform
            abortControllers: {}, // Store AbortController instances for each platform to enable cancellation
            showResearchNotification: true,
            isShowResearchFlow: false,
            researchFlowStep: 1,
            interviewReward: "20",
            maxInterviewRewards: "15",
            surveyReward: "0",
            maxSurveyRewards: "0", // 1: Add your idea, 2: Research Path, 3: Build contact list
            venture: null,
            isPageLoading: false,
            selectedItems: [],
            onDone: null,
            isDeleting: false,
        };
    },
    computed: {
        opportunityMapData() {
            return this.getOpportunityMapData();
        },
        interviewBudget() {
            const reward = parseFloat(this.interviewReward) || 0;
            const maxRewards = parseInt(this.maxInterviewRewards) || 0;
            return reward * maxRewards;
        },
        surveyBudget() {
            const reward = parseFloat(this.surveyReward) || 0;
            const maxRewards = parseInt(this.maxSurveyRewards) || 0;
            return reward * maxRewards;
        },
        surveyBudgetAmount() {
            const reward = parseFloat(this.surveyRewardAmount) || 0;
            const maxRewards = parseInt(this.maxSurveyRewardsAmount) || 0;
            return reward * maxRewards;
        },
        groupedOutreachAreas() {
            if (
                !this.targetCustomerUser?.outreachAreas ||
                this.targetCustomerUser.outreachAreas.length === 0
            ) {
                return {};
            }

            // Group outreach areas by platform
            const grouped = {};
            this.targetCustomerUser.outreachAreas.forEach((area) => {
                const platform = area.platform || area.name || area;
                if (!grouped[platform]) {
                    grouped[platform] = [];
                }
                grouped[platform].push(area);
            });

            return grouped;
        },
        hasExistingResearch() {
            // Check if surveys or interviews already exist
            const hasSurveys = this.targetCustomerUser?.surveys?.length > 0;
            const hasInterviews =
                this.targetCustomerUser?.interviews?.length > 0;
            return hasSurveys || hasInterviews;
        },
        allPlatformsHaveContacts() {
            // Check if all platforms have at least 1 contact
            const platforms = Object.keys(this.groupedOutreachAreas);
            if (platforms.length === 0) return false;

            return platforms.every((platform) => {
                return this.contactCounts[platform] > 0;
            });
        },
        isBudgetSet() {
            // Check if budget values are already set
            return (
                this.targetCustomerUser?.interviewRewardAmount ||
                this.targetCustomerUser?.surveyRewardAmount ||
                this.targetCustomerUser?.interviewRewardMax ||
                this.targetCustomerUser?.surveyRewardMax
            );
        },
        groupedContacts() {
            // Group contacts by platform
            if (
                !this.targetCustomerUser?.contacts ||
                this.targetCustomerUser.contacts.length === 0
            ) {
                return {};
            }

            const grouped = {};
            this.targetCustomerUser.contacts.forEach((contact) => {
                // Get platform from social handles or default to 'Other'
                const platform =
                    contact.socialHandles?.[0]?.platform || "Other";
                if (!grouped[platform]) {
                    grouped[platform] = [];
                }
                grouped[platform].push(contact);
            });

            return grouped;
        },
    },
    watch: {
        hasExistingResearch: {
            handler(newVal) {
                // Auto-advance to step 2 if research already exists and we're on step 1
                if (newVal && this.researchFlowStep === 1) {
                    this.researchFlowStep = 2;
                }
            },
            immediate: true,
        },
        selectedSurveyOpportunities: {
            handler(newVal) {
                // Watcher for selectedSurveyOpportunities changes
            },
            immediate: true,
        },
        isShowOpportunitySelect: {
            handler(newVal) {
                // Watcher for isShowOpportunitySelect changes
            },
            immediate: true,
        },
        isShowSurveyFlow: {
            handler(newVal) {
                if (newVal) {
                    // Survey flow modal opened - initialize data if needed
                }
            },
            immediate: true,
        },
        allPlatformsHaveContacts: {
            handler(newVal) {
                // Auto-advance to step 3 if all platforms have contacts and we're on step 2
                if (newVal && this.researchFlowStep === 2) {
                    this.researchFlowStep = 3;
                }
            },
            immediate: true,
        },
        selectedTab: function (val) {
            this.editor.commands.setContent(val.body, false);
        },
        survey: {
            handler(val) {
                this.getCompleteSurveys();
            },
            deep: true,
        },
        $route: {
            handler() {
                this.openOpportunityMapFromUrl();
            },
            immediate: false,
        },
    },
    methods: {
        goToOpportunityMap() {
            this.isShowOpportunityMap = true;
        },
        cancelContactSearch(platform) {
            console.log(`🛑 Cancelling contact search for ${platform}`);

            // Abort the ongoing request if it exists
            if (this.abortControllers[platform]) {
                this.abortControllers[platform].abort();
                console.log(`✅ Aborted request for ${platform}`);
            }

            // Clear loading state
            this.loadingStates[platform] = false;

            // Reset contact count to original count from database
            this.contactCounts[platform] =
                this.targetCustomerUser?.contacts?.length || 0;

            // Mark search as not started
            this.hasStartedContactSearch[platform] = false;

            // Clear the abort controller reference
            delete this.abortControllers[platform];

            // Save the cancelled state to database
            this.saveContactSearchState(platform, "cancelled", 0);

            console.log(`🛑 Contact search cancelled for ${platform}`);
        },
        async findContacts(platform, platformGroup) {
            console.log(`🚀 Starting contact search for ${platform}`);
            console.log(`📊 Current loadingStates:`, this.loadingStates);
            console.log(`📊 Current contactCounts:`, this.contactCounts);

            // Create AbortController for this search
            const abortController = new AbortController();
            this.abortControllers[platform] = abortController;

            // Set loading state for this platform
            this.loadingStates[platform] = true;
            this.$forceUpdate(); // Force reactivity update

            // Mark that contact search has been started for this platform
            this.hasStartedContactSearch[platform] = true;

            // Initialize contact count from existing contacts in database
            if (!this.contactCounts[platform]) {
                this.contactCounts[platform] =
                    this.targetCustomerUser?.contacts?.length || 0;
            }

            // Save the search state to database
            await this.saveContactSearchState(platform, "searching");

            console.log(
                `📊 After initialization - loadingStates:`,
                this.loadingStates
            );
            console.log(
                `📊 After initialization - contactCounts:`,
                this.contactCounts
            );

            try {
                // Loop through all items in the platform group
                for (const area of platformGroup) {
                    console.log(`🔍 Finding contacts for ${platform}:`, area);

                    // Call contactList Lambda for each area
                    const result = await this.effects.lambda.getContactList(
                        area.platform || area.name || area,
                        area.link || "",
                        this.targetCustomerUser?.name || "",
                        abortController.signal
                    );

                    console.log(
                        `📊 ContactList result for ${platform}:`,
                        result
                    );

                    // Process Lambda result and save contacts to database
                    let contactsToSave = [];
                    let newContactCount = 0;

                    if (result && result.users && Array.isArray(result.users)) {
                        // If result has contacts array, save them to database
                        contactsToSave = result.users;
                        newContactCount = result.users.length;
                    }

                    // Save contacts to database if we have contact data
                    if (contactsToSave.length > 0) {
                        try {
                            console.log(
                                `💾 Saving ${contactsToSave.length} contacts to database for ${platform}`
                            );

                            await this.actions.venture.saveVentureContact({
                                ventureId: this.$route.params.ventureId,
                                targetCustomerUserId:
                                    this.targetCustomerUser.id,
                                contacts: contactsToSave,
                            });

                            // Only increment count after successful save
                            this.contactCounts[platform] += newContactCount;
                            console.log(
                                `✅ Successfully saved ${newContactCount} contacts for ${platform}`
                            );
                        } catch (saveError) {
                            console.error(
                                `❌ Error saving contacts for ${platform}:`,
                                saveError
                            );
                            // Don't increment count if save failed
                        }
                    } else {
                        // If no contact data to save, just add the count
                        this.contactCounts[platform] += newContactCount;
                    }
                }

                console.log(
                    `✅ Total contacts found for ${platform}:`,
                    this.contactCounts[platform]
                );
            } catch (error) {
                console.error(
                    `❌ Error finding contacts for ${platform}:`,
                    error
                );

                // Handle abort errors (user cancelled)
                if (error.name === "AbortError") {
                    console.log(`🛑 Contact search aborted for ${platform}`);
                    // Don't update contact counts or show error - just exit
                    return;
                }

                // Handle CORS errors gracefully
                if (error.message && error.message.includes("CORS")) {
                    // Set a mock contact count for development/testing
                    this.contactCounts[platform] =
                        Math.floor(Math.random() * 50) + 10; // Random number between 10-60
                    console.log(
                        `🔧 CORS Error - Using mock data for ${platform}: ${this.contactCounts[platform]} contacts`
                    );

                    // Show user notification about CORS issue
                    this.$toast?.error?.(
                        "CORS Error: Lambda function needs CORS headers configured. Using mock data for testing."
                    );
                } else {
                    // For any other error, still show 0 contacts found
                    this.contactCounts[platform] += 0;
                }
            } finally {
                // Clear loading state
                this.loadingStates[platform] = false;

                // Clean up abort controller
                delete this.abortControllers[platform];

                // Save the completed state to database
                await this.saveContactSearchState(
                    platform,
                    "completed",
                    this.contactCounts[platform]
                );

                this.$forceUpdate(); // Force reactivity update
            }
        },
        async saveContactSearchState(platform, status, contactCount = 0) {
            try {
                console.log(`💾 Saving contact search state for ${platform}:`, {
                    status,
                    contactCount,
                });

                // Update the outreachAreas with search state
                const updatedOutreachAreas =
                    this.targetCustomerUser.outreachAreas.map((area) => {
                        if ((area.platform || area.name || area) === platform) {
                            return {
                                ...area,
                                searchStatus: status,
                                contactCount: contactCount,
                                lastSearchedAt: new Date().toISOString(),
                            };
                        }
                        return area;
                    });

                // Save to database
                await this.actions.venture.saveTargetCustomerUser({
                    data: {
                        outreachAreas: updatedOutreachAreas,
                    },
                    where: { id: this.targetCustomerUser.id },
                });

                // Update local state
                this.targetCustomerUser.outreachAreas = updatedOutreachAreas;

                console.log(`✅ Contact search state saved for ${platform}`);
            } catch (error) {
                console.error(
                    `❌ Error saving contact search state for ${platform}:`,
                    error
                );
            }
        },
        loadContactSearchState() {
            console.log("🔄 Loading contact search state from database...");

            if (!this.targetCustomerUser?.outreachAreas) {
                console.log("⚠️ No outreach areas found");
                return;
            }

            // Initialize state objects
            this.loadingStates = {};
            this.hasStartedContactSearch = {};
            this.contactCounts = {};

            // Load state from outreachAreas
            this.targetCustomerUser.outreachAreas.forEach((area) => {
                const platform = area.platform || area.name || area;

                if (area.searchStatus) {
                    console.log(`📊 Loading state for ${platform}:`, {
                        status: area.searchStatus,
                        contactCount: area.contactCount,
                        lastSearchedAt: area.lastSearchedAt,
                    });

                    // Initialize contact count from actual contacts in database
                    this.contactCounts[platform] =
                        this.targetCustomerUser?.contacts?.length || 0;

                    // Set search started flag
                    if (
                        area.searchStatus === "searching" ||
                        area.searchStatus === "completed"
                    ) {
                        this.hasStartedContactSearch[platform] = true;
                    }

                    // Set loading state
                    if (area.searchStatus === "searching") {
                        this.loadingStates[platform] = true;
                    }
                }
            });

            console.log("✅ Contact search state loaded:", {
                loadingStates: this.loadingStates,
                hasStartedContactSearch: this.hasStartedContactSearch,
                contactCounts: this.contactCounts,
            });
        },
        async saveOutreachRewards() {
            console.log("💾 Saving outreach rewards...");
            console.log("📊 Current values:", {
                interviewReward: this.interviewReward,
                maxInterviewRewards: this.maxInterviewRewards,
                surveyReward: this.surveyReward,
                maxSurveyRewards: this.maxSurveyRewards,
                interviewBudget: this.interviewBudget,
                surveyBudget: this.surveyBudget,
            });

            this.isLoading = true;
            this.savingPart = "OutreachRewards";

            try {
                // Validate minimum values
                const interviewMax = parseInt(this.maxInterviewRewards) || 0;
                const surveyMax = parseInt(this.maxSurveyRewards) || 0;

                if (interviewMax < 20) {
                    this.$toast?.error?.(
                        "Max Interview Rewards must be at least 20"
                    );
                    return;
                }

                if (surveyMax < 30) {
                    this.$toast?.error?.(
                        "Max Survey Rewards must be at least 30"
                    );
                    return;
                }

                const budgetData = {
                    interviewRewardAmount:
                        parseFloat(this.interviewReward) || 0,
                    interviewRewardMax: interviewMax,
                    interviewBudget: this.interviewBudget,
                    surveyRewardAmount: parseFloat(this.surveyReward) || 0,
                    surveyRewardMax: surveyMax,
                    surveyBudget: this.surveyBudget,
                };

                console.log("📊 Budget data to save:", budgetData);

                const result =
                    await this.actions.venture.saveTargetCustomerUser({
                        data: budgetData,
                        where: { id: this.targetCustomerUser.id },
                    });

                console.log("✅ Outreach rewards saved:", result);

                // Update local state
                this.targetCustomerUser = {
                    ...this.targetCustomerUser,
                    ...budgetData,
                };

                console.log(
                    "🔄 Updated targetCustomerUser:",
                    this.targetCustomerUser
                );

                // Show success message
                this.$toast?.success?.("Outreach rewards saved successfully!");
            } catch (error) {
                console.error("❌ Error saving outreach rewards:", error);
                this.$toast?.error?.(
                    "Failed to save outreach rewards. Please try again."
                );
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        onSelectOpportunity(opportunity) {
            const opportunities = [...this.selectedOpportunities];
            const index = opportunities?.findIndex(
                (o) => o.id === opportunity?.id
            );

            if (index > -1) {
                // Remove if already selected
                opportunities?.splice(index, 1);
            } else {
                // Check if we already have 3 opportunities selected
                if (opportunities.length >= 3) {
                    this.$toast?.error?.(
                        "You can only select up to 3 opportunities for your survey"
                    );
                    return;
                }
                // Add if not selected and under limit
                opportunities.push(opportunity);
            }

            this.selectedOpportunities = opportunities;
            this.selectedSurveyOpportunities = [...opportunities];
        },
        async onAddOpportunities(data) {
            // TODO: Implement opportunity creation logic

            // Only close the dropdown if we're not in survey flow
            // In survey flow, we want to keep it open so users can select multiple opportunities
            if (!this.isShowSurveyFlow) {
                this.isShowOpportunitySelect = false;
                this.selectedOpportunities = [];
            }
        },
        openOpportunitySelector() {
            // TODO: Implement opportunity selector modal
        },
        removeSurveyOpportunity(opportunityId) {
            this.selectedSurveyOpportunities =
                this.selectedSurveyOpportunities.filter(
                    (opp) => opp.id !== opportunityId
                );
            // Also update the temporary selectedOpportunities list
            this.selectedOpportunities = this.selectedOpportunities.filter(
                (opp) => opp.id !== opportunityId
            );
        },
        async createSurvey() {
            // Validate that exactly 3 opportunities are selected
            if (this.selectedSurveyOpportunities.length !== 3) {
                this.$toast?.error?.(
                    "Please select exactly 3 opportunities for your survey"
                );
                return;
            }

            try {
                this.isLoading = true;
                this.savingPart = "SURVEY";

                // Prepare opportunities data
                const opportunitiesData = this.selectedSurveyOpportunities.map(
                    (opp) => ({
                        id: opp.id,
                        name: opp.name,
                    })
                );

                // Call the buildVentureSurvey mutation
                const result = await this.actions.venture.buildVentureSurvey({
                    ventureId: this.$route.params.ventureId,
                    targetCustomerUserId: this.targetCustomerUser.id,
                });

                // Save survey reward values to target customer user
                await this.saveSurveyRewards();

                this.$toast?.success?.("Survey created successfully!");

                // Advance to step 2
                this.surveyFlowStep = 2;
            } catch (error) {
                console.error("Error creating survey:", error);
                this.$toast?.error?.(
                    "Failed to create survey. Please try again."
                );
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async saveSurveyRewards() {
            try {
                const surveyRewardData = {
                    surveyRewardAmount:
                        parseFloat(this.surveyRewardAmount) || 0,
                    surveyRewardMax:
                        parseInt(this.maxSurveyRewardsAmount) || 30,
                    surveyBudget: this.surveyBudgetAmount,
                };

                await this.actions.targetCustomerUser.saveTargetCustomerUser({
                    where: { id: this.targetCustomerUser.id },
                    data: surveyRewardData,
                });

                // Update local state
                this.targetCustomerUser = {
                    ...this.targetCustomerUser,
                    ...surveyRewardData,
                };
            } catch (error) {
                console.error("Error saving survey rewards:", error);
                this.$toast?.error?.(
                    "Failed to save survey rewards. Please try again."
                );
            }
        },
        startResearch() {
            // Close the notification
            this.showResearchNotification = false;
            // Close the opportunity map modal
            this.isShowOpportunityMap = false;
            // Open the research flow modal
            this.isShowResearchFlow = true;
        },
        async selectResearchPath(path) {
            console.log(`Selected research path: ${path}`);

            try {
                // Prepare targetCustomerUser data
                const targetCustomerUserData = {
                    id: this.targetCustomerUser?.id,
                    name: this.targetCustomerUser?.name,
                    researchPrompt: this.targetCustomerUser?.researchPrompt,
                };

                // Call the mutation based on the selected path
                if (path === "interviews") {
                    // Interviews + Surveys: interviews: true, surveys: true
                    await this.actions.venture.saveVentureResearchPath({
                        interviews: true,
                        surveys: true,
                        targetCustomerUser: targetCustomerUserData,
                        ventureId: this.$route.params.ventureId,
                        userId: this.state.currentUser.id,
                        creatorEmail:
                            this.venture?.createdBy?.email ||
                            this.state.currentUser?.email,
                    });
                } else if (path === "surveys") {
                    // Surveys Only: interviews: false, surveys: true
                    await this.actions.venture.saveVentureResearchPath({
                        interviews: false,
                        surveys: true,
                        targetCustomerUser: targetCustomerUserData,
                        ventureId: this.$route.params.ventureId,
                        userId: this.state.currentUser.id,
                        creatorEmail:
                            this.venture?.createdBy?.email ||
                            this.state.currentUser?.email,
                    });

                    // Open survey flow modal instead of advancing to step 2
                    this.isShowResearchFlow = false; // Close research flow modal
                    this.isShowSurveyFlow = true;
                    this.surveyFlowStep = 1;

                    // Reset survey opportunity selection data
                    this.selectedSurveyOpportunities = [];
                    this.selectedOpportunities = [];
                    console.log(
                        "Reset survey data - selectedSurveyOpportunities:",
                        this.selectedSurveyOpportunities
                    );
                    console.log(
                        "Available opportunities:",
                        this.targetCustomerUser?.opportunities
                    );

                    return; // Don't advance to step 2
                }

                console.log("Research path saved successfully");
            } catch (error) {
                console.error("Error saving research path:", error);
            }

            // Advance to step 2: Build contact list (only for interviews path)
            this.researchFlowStep = 2;
        },
        saveOutreachPlan() {
            console.log("Saving outreach plan...");
            console.log("Interview Budget:", this.interviewBudget);
            console.log("Survey Budget:", this.surveyBudget);

            // Close the research flow modal
            this.isShowResearchFlow = false;

            // Navigate to contacts tab for outreach setup
            this.selectedNav = this.getTabItems().find(
                (tab) => tab.name === "Contacts"
            );
        },
        shareOpportunityMap() {
            // Generate shareable URL with opportunity map query parameter
            const shareUrl = `${window.location.origin}${window.location.pathname}?showOpportunityMap=true`;

            // Copy to clipboard
            navigator.clipboard
                .writeText(shareUrl)
                .then(() => {
                    // You can add a toast notification here
                    console.log(
                        "Opportunity Map URL copied to clipboard:",
                        shareUrl
                    );
                    // Example: this.actions.alert.showSuccess({ message: "Link copied to clipboard!" });
                })
                .catch((err) => {
                    console.error("Failed to copy URL:", err);
                    // Fallback: show the URL in an alert
                    alert(`Share this URL: ${shareUrl}`);
                });
        },
        showMoreDetails() {
            // Close the opportunity map modal
            this.isShowOpportunityMap = false;
            // Navigate to the Details tab to show more information
            this.selectedNav = this.getTabItems().find(
                (tab) => tab.name === "Details"
            );
        },
        openOpportunityMapFromUrl() {
            if (this.$route.query.showOpportunityMap === "true") {
                this.isShowOpportunityMap = true;
                // Clear the query parameter from URL without triggering navigation
                this.$router.replace({
                    name: this.$route.name,
                    params: this.$route.params,
                    query: {},
                });
            }
        },
        getOpportunityMapData() {
            if (
                !this.targetCustomerUser?.opportunities ||
                this.targetCustomerUser.opportunities.length === 0
            ) {
                // Use the same sample data as TestPage.vue
                return {
                    nodes: opportunity,
                    links: opportunityLinks,
                    nodeMapping: {
                        feature: "gray",
                        opportunity: "blue",
                        solution: "green",
                    },
                    legend: [
                        {
                            id: 1,
                            type: "solution",
                            label: "How",
                        },
                        {
                            id: 2,
                            type: "opportunity",
                            label: "Why",
                        },
                    ],
                    title: '<span class="font-semibold text-gray-800">Adults in physical therapy <span class="font-medium text-gray-600">and how they manage their exercises at home</span></span>',
                    btnLabel: "View Table",
                    btnIcon: "ViewListIcon",
                    dialogTitle: "keep track what exercises to do",
                    label: "Adults in physical therapy need to..",
                    badgeText: "Ability",
                    detailDialogTitle: "Survey Summary",
                    navigationGraph: {
                        experience: {
                            name: "Experience",
                            percent: "60",
                            value: "-1.9",
                        },
                        magnitude: {
                            name: "Magnitude",
                            percent: "49.9",
                        },
                        desire: {
                            name: "Desire",
                            percent: "80",
                            value: "+2.5",
                        },
                        outOfMarket: {
                            name: "Out Of Market",
                            percent: "35%",
                        },
                        inMarket: {
                            name: "In Market",
                            percent: "65%",
                        },
                        earlyAdopter: {
                            name: "Early Adopter",
                            percent: "12%",
                        },
                    },
                    demographics: [
                        {
                            title: "",
                            content: [
                                {
                                    name: "Out",
                                    count: 13,
                                    color: "gray",
                                    tooltip: [
                                        "Out Motivation (1-24)",
                                        "13 people (30.3%)",
                                    ],
                                },
                                {
                                    name: "Low",
                                    count: 13,
                                    color: "orange",
                                    tooltip: [
                                        "Low Motivation (1-24)",
                                        "13 people (30.3%)",
                                    ],
                                },
                                {
                                    name: "Mid",
                                    count: 7,
                                    color: "yellow",
                                    tooltip: [
                                        "Mid Motivation (1-24)",
                                        "13 people (30.3%)",
                                    ],
                                },
                                {
                                    name: "High",
                                    count: 3,
                                    color: "green",
                                    tooltip: [
                                        "High Motivation (1-24)",
                                        "13 people (30.3%)",
                                    ],
                                },
                            ],
                        },
                    ],
                    tables: [
                        {
                            title: "Table title",
                            header: [],
                            body: [
                                ["Magnitude", "29.9"],
                                ["Desire", "+2.5"],
                                ["Experience", "-1.9"],
                                ["Activity", "95%"],
                                ["Frequency", "12x/week"],
                            ],
                        },
                        {
                            header: [],
                            body: [
                                ["Out of Market", "35%"],
                                ["In Market", "65%"],
                                ["Adjusted Market", "18,000,000"],
                                ["Early Adopters", "12%"],
                                ["Early Adopter Size", "3,300,000"],
                            ],
                        },
                    ],
                };
            }

            const opportunities = this.targetCustomerUser.opportunities.filter(
                (o) => !o.isArchived
            );

            // Transform opportunities into nodes
            const nodes = opportunities.map((opportunity) => ({
                id: opportunity.id,
                name: opportunity.name,
                type: opportunity.type || "opportunity",
                count: opportunity.interviews?.length || 0,
                active: true,
                ...opportunity,
            }));

            // Transform opportunity links into edges
            const links = [];
            opportunities.forEach((opportunity) => {
                // Add parent links
                if (opportunity.parentOpportunityLinks) {
                    opportunity.parentOpportunityLinks.forEach((link) => {
                        if (link.parent && !link.parent.isArchived) {
                            links.push({
                                id: link.id,
                                parentId: link.parent.id,
                                childId: opportunity.id,
                                type: "smoothstep",
                            });
                        }
                    });
                }

                // Add child links
                if (opportunity.childOpportunityLinks) {
                    opportunity.childOpportunityLinks.forEach((link) => {
                        if (link.child && !link.child.isArchived) {
                            links.push({
                                id: link.id,
                                parentId: opportunity.id,
                                childId: link.child.id,
                                type: "smoothstep",
                            });
                        }
                    });
                }
            });

            // Create node mapping
            const nodeMapping = {
                opportunity: "blue",
                feature: "gray",
                solution: "green",
            };

            // Create legend
            const legend = [
                {
                    id: 1,
                    type: "opportunity",
                    label: "Opportunities",
                },
                {
                    id: 2,
                    type: "feature",
                    label: "Features",
                },
                {
                    id: 3,
                    type: "solution",
                    label: "Solutions",
                },
            ];

            // Create demographics data from survey responses
            const demographics = [];
            if (
                this.targetCustomerUser.surveys &&
                this.targetCustomerUser.surveys.length > 0
            ) {
                const survey = this.targetCustomerUser.surveys[0];
                if (survey.surveyOpportunities) {
                    survey.surveyOpportunities.forEach((surveyOpp) => {
                        if (
                            surveyOpp.responses &&
                            surveyOpp.responses.length > 0
                        ) {
                            const responses = surveyOpp.responses.filter(
                                (r) => !r.isArchived
                            );
                            if (responses.length > 0) {
                                demographics.push({
                                    title: surveyOpp.opportunity.name,
                                    content: [
                                        {
                                            name: "Completed",
                                            count: responses.length,
                                            color: "green",
                                            tooltip: [
                                                `Completed surveys`,
                                                `${responses.length} responses`,
                                            ],
                                        },
                                    ],
                                });
                            }
                        }
                    });
                }
            }

            // Create tables data
            const tables = [];
            opportunities.forEach((opportunity) => {
                if (
                    opportunity.interviews &&
                    opportunity.interviews.length > 0
                ) {
                    tables.push({
                        title: opportunity.name,
                        data: opportunity.interviews.map((interview) => ({
                            Contact: `${
                                interview.contact?.user?.firstName || ""
                            } ${interview.contact?.user?.lastName || ""}`,
                            Date: new Date(
                                interview.scheduledDateTime
                            ).toLocaleDateString(),
                            Status: interview.isArchived
                                ? "Archived"
                                : "Active",
                        })),
                    });
                }
            });

            return {
                nodes,
                links,
                nodeMapping,
                legend,
                demographics,
                tables,
                title: `Opportunity Map - ${this.targetCustomerUser.name}`,
                btnLabel: "View Details",
                btnIcon: "ViewListIcon",
                btnClick: () => {},
                dialogTitle: "Opportunity Details",
                label: `${this.targetCustomerUser.name} opportunities`,
                badgeText: "Opportunity",
                detailDialogTitle: "Opportunity Details",
            };
        },
        getTargetTags() {
            const tags = this.targetCustomerUser?.targetCustomer?.tags;
            // const locations = tags?.filter((t) => t.type === "LOCATION");
            // const list = [];
            // if (locations.find((l) => l.category === "CITY")?.name) {
            //     list.push(locations.find((l) => l.category === "CITY")?.name);
            // }
            // if (locations.find((l) => l.category === "STATE")?.name) {
            //     list.push(locations.find((l) => l.category === "STATE")?.name);
            // }
            // if (
            //     locations.find((l) => l.category === "COUNTRY")?.name &&
            //     list.length === 0
            // ) {
            //     list.push(
            //         locations.find((l) => l.category === "COUNTRY")?.name
            //     );
            // }
            const list = [];
            const locations = tags?.filter((t) => t?.type === "LOCATION");
            if (
                locations.find((l) => l?.category === "COUNTRY") &&
                locations?.length === 1
            ) {
                locations?.map((tag) => {
                    list.push({
                        category: "Market",
                        name: tag?.name,
                        confirmationQuestion: tag?.confirmationQuestion,
                    });
                });
            } else {
                locations
                    ?.filter((l) => l?.category !== "COUNTRY")
                    .map((tag) => {
                        list?.push({
                            category: "Market",
                            name: tag?.name,
                            confirmationQuestion: tag?.confirmationQuestion,
                        });
                    });
            }
            // const tag = {
            //     category: "Market",
            //     name: `${list.join(", ")}`,
            //     confirmationQuestion: `Do you live in ${list.join(", ")}?`,
            // };
            return [...tags.filter((t) => t.type !== "LOCATION"), ...list];
        },
        async onArchive() {
            this.isDeleting = true;
            try {
                await this.actions.profile.archiveProfile({
                    bulkDeleteProfileIds: this.selectedItems?.map(
                        (item) => item?.id
                    ),
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.getContacts();
                this.onDone();
                this.actions.alert.showSuccess({
                    message: `Archived ${items.length} Contacts successfully!`,
                });
                this.selectedItems = [];
            } catch (error) {
                console.log(error);
            } finally {
                this.isDeleting = false;
            }
        },
        getAnswer(answer) {
            if (answer.includes("#")) {
                const items = answer.split(" ");
                items.shift();
                return items.join(" ");
            } else {
                return answer;
            }
        },
        getOpportunitiesList(question, surveyOpportunity) {
            const questions = surveyOpportunity?.surveyQuestionGroup?.questions;
            console.log(question, "question", this.problems);
            if (
                question?.category?.includes("Motivator") ||
                question?.category?.includes("Solution")
            ) {
                return this.solutions.filter(
                    (m) =>
                        m?.id !== surveyOpportunity?.opportunity?.id &&
                        !questions
                            ?.find((q) => q?.category?.includes("Motivator"))
                            ?.answerOptions?.find((a) =>
                                m.name.includes(a.answer)
                            ) &&
                        !questions
                            ?.find((q) => q?.category?.includes("Solution"))
                            ?.answerOptions?.find((a) =>
                                m.name.includes(a.answer)
                            )
                );
            } else {
                return this.problems.filter(
                    (o) =>
                        !question?.answerOptions?.find(
                            (a) => a?.answer === o?.label
                        )
                );
            }
        },
        async onDragEnd(e) {
            console.log(this.survey.surveyOpportunities, "test");
            const data = [];
            this.survey.surveyOpportunities?.map((s, i) => {
                data.push({
                    where: { id: s?.id },
                    data: { sortOrder: i },
                });
            });
            this.actions.survey.saveSurvey({
                where: { id: this.survey?.id },
                data: {
                    surveyOpportunities: {
                        update: data,
                    },
                },
            });
        },
        async onSetOpportunityIdToMetadata(
            options,
            i,
            surveyOpportunity,
            question
        ) {
            console.log(options[i]);
            if (options[i]?.id) {
                await this.actions.surveyOpportunity.saveSurveyOpportunity({
                    where: { id: surveyOpportunity?.id },
                    data: {
                        surveyQuestionGroup: {
                            update: {
                                questions: {
                                    update: [
                                        {
                                            where: { id: question?.id },
                                            data: {
                                                answerOptions: {
                                                    update: [
                                                        {
                                                            where: {
                                                                id: options[i]
                                                                    ?.id,
                                                            },
                                                            data: {
                                                                metadata: {
                                                                    ...options[
                                                                        i
                                                                    ].metadata,
                                                                    opportunityId:
                                                                        options[
                                                                            i
                                                                        ]
                                                                            ?.metadata
                                                                            ?.temporaryOpportunityId,
                                                                },
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                        },
                    },
                });
                options[i].metadata = {
                    ...options[i].metadata,
                    opportunityId: options[i]?.metadata?.temporaryOpportunityId,
                };
            }
        },
        async onDeleteAnswer(options, i, surveyOpportunity, question) {
            if (options[i]?.id) {
                await this.actions.surveyOpportunity.saveSurveyOpportunity({
                    where: { id: surveyOpportunity?.id },
                    data: {
                        surveyQuestionGroup: {
                            update: {
                                questions: {
                                    update: [
                                        {
                                            where: { id: question?.id },
                                            data: {
                                                answerOptions: {
                                                    delete: [
                                                        { id: options[i]?.id },
                                                    ],
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                        },
                    },
                });
            }
            options.splice(i, 1);
        },
        onEditAnswer(option) {
            option.isEdit = true;
            setTimeout(() => {
                document.getElementById(slugify(option.answer)).focus();
            }, 100);
        },
        async onCopyUrl(url) {
            const { toClipboard } = useClipboard();
            try {
                await toClipboard(url);
                this.actions.alert.showSuccess({
                    message: "Copied the url to your clipboard",
                });
            } catch (e) {
                console.error(e);
            }
        },
        getDemographicQuestions() {
            const questions = c.demographicQuestions.slice(0, 5);
            questions.push({
                label: "Would you be open to talking more about this topic?",
                type: "RADIO",
                options: [
                    { id: 0, title: "Yes" },
                    { id: 1, title: "No" },
                ],
                value: null,
            });
            return questions;
        },
        onClickPreview(type, data) {
            let category = type;
            if (type === "SURVEY") {
                category += `_${data.id}`;
            }
            console.log(window.location);
            const url =
                window.location.origin +
                `/#/survey/${this.survey?.id}?category=${category}`;
            window.open(url, "_blank");
        },
        async onCreateInterviewScript() {
            if (this.isAdding) return false;
            this.isAdding = true;
            await this.actions.venture.saveVenture({
                where: { id: this.$route.params.ventureId },
                data: {
                    targetCustomerUsers: {
                        update: [
                            {
                                where: { id: this.targetCustomerUser?.id },
                                data: {
                                    interviewScripts: {
                                        create: [
                                            {
                                                name:
                                                    "New Script " +
                                                    moment().format("MM/DD"),
                                                introScript: `<ul><li><p>Hi {{ firstName }}, thank you so much for being generous with your time. I'll do my best to keep this to just 30 minutes.</p></li>
                                                                <li><p>Like I said, I'm talking with {{targetCustomerUser.name}} like you about how they {{researchPrompt}}.</p></li>
                                                                <li><p>I'm going to ask you a few questions and mostly just want to get to know a bit of your story.</p></li>
                                                                <li><p>Would you mind if I record this so I can take notes later? It's completely ok if you'd rather not.</p></li>
                                                            </ul>`,
                                                questionScript: `<ul><li><p>How do you/When was the last time you had to {{researchPrompt}}?</p></li>
                                                                <li><p>Tell me a little about what led up to that...where you were, who you were with, what was going on in your life (this is to help jog their memory)</p></li>
                                                                <li><p>Now tell me about that experience...walk me through it.</p></li>
                                                                <li><p>What did you expect going into it/How did you feel about it after?</p></li></ul>
                                                                <br><br>
                                                                FOLLOW UPS
                                                                <br><br>
                                                                <ul><li><p>You mentioned that you [do something], tell me more about that</p></li>
                                                                <li><p>How did you come about doing things in that way?</p></li>
                                                                <li><p>What do you like/not like about doing things that way?</p></li>
                                                                <li><p>What else do you use that for?</p></li>
                                                                <li><p>Tell me more about [that person you mentioned]</p></li>
                                                                <li><p>Tell me more about why you feel that way?</p></li>
                                                                <li><p>It sounds like things changed over time - why is that?</p></li>
                                                                <li><p>Have you ever had to do that and it went really well/poorly?</p></li>
                                                                <li><p>If you had a magic wand, what would you change about that experience?</p></li>
                                                            </ul>`,
                                                outroScript: `<ul><li><p>Thanks so much, {{firstName}}!</p></li>
                                                                <li><p>If it's ok with you, in a week or two I'll follow up with a short 30 second survey just to confirm some of my findings.</p></li>
                                                                <li><p>I'd also be happy to share what I've learned with you in the next few weeks if you'd be interested.</p></li>
                                                                <li><p>Last thing: Do you know any else who also does these types of things that might be willing to talk? I'd be really thankful if you could forward an email to them from me if you're willing.</p></li>
                                                            </ul>`,
                                                metadata: {
                                                    interviewScriptParams: [
                                                        "firstName",
                                                        "researchPrompt",
                                                    ],
                                                },
                                                isArchived: false,
                                                createdBy: {
                                                    connect: {
                                                        id: this.state
                                                            .currentUser?.id,
                                                    },
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    },
                },
            });
            const ventures = await this.actions.venture.getVentures({
                where: { id: this.$route.params?.ventureId },
                getValues: true,
                query: "ventureTargetCustomerDetail",
            });
            this.venture = ventures?.[0];
            this.targetCustomerUser = this.venture.targetCustomerUsers?.find(
                (t) => t?.id === this.targetCustomerUser?.id
            );
            this.isEditScriptName = true;
            this.selectedInterviewScript = json(
                this.targetCustomerUser?.interviewScripts
            )?.reverse()[0];
            this.isAdding = false;
            setTimeout(() => {
                document
                    .getElementById(this.selectedInterviewScript?.id)
                    .focus();
            }, 100);
        },
        async onDeleteScript(script) {
            if (this.targetCustomerUser?.interviewScripts?.length === 1) {
                this.actions.alert.showError({
                    message: "You can't delete the default script",
                });
                return false;
            }

            await this.actions.venture.saveVenture({
                where: { id: this.$route.params.ventureId },
                data: {
                    targetCustomerUsers: {
                        update: [
                            {
                                where: { id: this.targetCustomerUser?.id },
                                data: {
                                    interviewScripts: {
                                        delete: [
                                            {
                                                id: script?.id,
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    },
                },
            });
            const ventures = await this.actions.venture.getVentures({
                where: { id: this.$route.params?.ventureId },
                getValues: true,
                query: "ventureTargetCustomerDetail",
            });
            this.venture = ventures?.[0];
            this.targetCustomerUser = this.venture.targetCustomerUsers?.find(
                (t) => t?.id === this.targetCustomerUser?.id
            );
            this.selectedInterviewScript = json(
                this.targetCustomerUser?.interviewScripts
            )?.reverse()[0];
            setTimeout(() => {
                document
                    .getElementById(this.selectedInterviewScript?.id)
                    .focus();
            }, 100);
        },
        getFilterAndSort(data) {
            try {
                let items = [...data];
                if (this.searchText) {
                    items = items.filter((item) =>
                        item?.detail?.name
                            .toLowerCase()
                            .includes(this.searchText?.trim().toLowerCase())
                    );
                }
                if (this.filter?.parent?.name === "Type") {
                    if (this.filter?.child?.name) {
                        items = items?.filter(
                            (t) =>
                                t?.detail?.type ===
                                this.filter?.child?.name?.toUpperCase()
                        );
                    } else {
                        items = items?.filter((t) => t?.detail?.type);
                    }
                }
                if (this.sort?.parent?.name === "Name") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.name > b?.detail?.name ? 1 : -1;
                        } else {
                            return a?.detail?.name > b?.detail?.name ? -1 : 1;
                        }
                    });
                } else if (this.sort?.parent?.name === "Linked Opportunities") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.parentOpportunityLinks?.length +
                                a?.detail?.childOpportunityLinks?.length >
                                b?.detail?.parentOpportunityLinks?.length +
                                    b?.detail?.childOpportunityLinks?.length
                                ? 1
                                : -1;
                        } else {
                            return a?.detail?.parentOpportunityLinks?.length +
                                a?.detail?.childOpportunityLinks?.length >
                                b?.detail?.parentOpportunityLinks?.length +
                                    b?.detail?.childOpportunityLinks?.length
                                ? -1
                                : 1;
                        }
                    });
                } else if (this.sort?.parent?.name === "Interviews") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.interviews?.filter(
                                (o) => !o?.isArchived
                            )?.length >
                                b?.detail?.interviews?.filter(
                                    (o) => !o?.isArchived
                                )?.length
                                ? 1
                                : -1;
                        } else {
                            return a?.detail?.interviews?.filter(
                                (o) => !o?.isArchived
                            )?.length >
                                b?.detail?.interviews?.filter(
                                    (o) => !o?.isArchived
                                )?.length
                                ? -1
                                : 1;
                        }
                    });
                } else if (this.sort?.parent?.name === "Survey Responses") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.surveys?.length >
                                b?.detail?.surveys?.length
                                ? 1
                                : -1;
                        } else {
                            return a?.detail?.surveys?.length >
                                b?.detail?.surveys?.length
                                ? -1
                                : 1;
                        }
                    });
                } else if (this.sort?.parent?.name === "Magnitude") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.magnitudeScore >
                                b?.detail?.magnitudeScore
                                ? 1
                                : -1;
                        } else {
                            return a?.detail?.magnitudeScore >
                                b?.detail?.magnitudeScore
                                ? -1
                                : 1;
                        }
                    });
                } else if (this.sort?.parent?.name === "Created Date") {
                    items = items.sort((a, b) => {
                        if (this.sort?.child?.id === 0) {
                            return a?.detail?.createdAt > b?.detail?.createdAt
                                ? 1
                                : -1;
                        } else {
                            return a?.detail?.createdAt > b?.detail?.createdAt
                                ? -1
                                : 1;
                        }
                    });
                }
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onGoogle() {
            window.open(
                "https://www.google.com/search?q=number+of+" +
                    this.targetCustomerUser?.name?.toLowerCase() +
                    "+in+US",
                "_blank"
            );
        },
        handleTagForLocation(connects, create, disconnects, location, type) {
            const item = this.state.tag.tags.find(
                (t) => t?.name === location && t?.type === "LOCATION"
            );
            const update = {
                type: "LOCATION",
                category: type.toUpperCase(),
                name: location,
                nameLower: location.toLowerCase(),
                status: "APPROVED",
                targetType: "ALL",
            };
            if (item?.id) {
                connects.push({ id: item?.id });
                if (disconnects?.find((d) => d?.id === item?.id)) {
                    disconnects.splice(
                        disconnects.findIndex((d) => d?.id === item?.id),
                        1
                    );
                }
            } else create.push(update);
        },
        async onSaveName() {
            if (this.isLoading) return;
            this.isLoading = true;
            this.savingPart = "Detail";
            const disconnects = [],
                connects = [];
            const fields = this.formFieldGroup[0];
            const tags = [
                fields.age.value,
                fields.gender.value,
                fields.ethnicity.value,
                fields.income.value,
                ...fields.markets.value,
                fields.estimatedAnnualIncome.value,
                fields.employeeSize.value,
                ...fields.interestAndActivities.value,
            ];
            try {
                tags?.filter(
                    (d) =>
                        !this.targetCustomerUser?.targetCustomer?.tags?.find(
                            (t) => t?.id === d?.id
                        )
                ).map(
                    (tag) =>
                        tag?.id &&
                        isNaN(tag?.id) &&
                        connects.push({ id: tag.id })
                );
                this.targetCustomerUser?.targetCustomer?.tags
                    ?.filter(
                        (d) => !tags?.find((t) => t?.id && t?.id === d?.id)
                    )
                    .map((tag) => disconnects.push({ id: tag?.id }));
                const create = [];
                for (let i = 0; i < tags?.length; i++) {
                    if (!tags[i]?.id && tags[i]?.place_id) {
                        const res =
                            await this.actions.google.searchGooglePlaceById({
                                placeId: tags[i]?.place_id,
                            });
                        var city, state, country;
                        res?.address_components?.map((addressComponent) => {
                            if (
                                _.indexOf(
                                    addressComponent.types,
                                    "neighborhood"
                                ) > -1
                            )
                                city = addressComponent["short_name"];
                            if (
                                _.indexOf(addressComponent.types, "locality") >
                                -1
                            )
                                city = addressComponent["short_name"];
                            if (
                                _.indexOf(
                                    addressComponent.types,
                                    "administrative_area_level_1"
                                ) > -1
                            )
                                state = addressComponent["short_name"];
                            if (
                                _.indexOf(addressComponent.types, "country") >
                                -1
                            )
                                country = addressComponent["short_name"];
                        });
                        if (city) {
                            this.handleTagForLocation(
                                connects,
                                create,
                                disconnects,
                                city,
                                "city"
                            );
                        }
                        if (state) {
                            this.handleTagForLocation(
                                connects,
                                create,
                                disconnects,
                                state,
                                "state"
                            );
                        }
                        if (country) {
                            this.handleTagForLocation(
                                connects,
                                create,
                                disconnects,
                                country,
                                "country"
                            );
                        }
                    }
                }
                tags.filter((t) => !isNaN(t?.id)).map((t) => {
                    create.push({
                        type: "DEMOGRAPHIC",
                        category: "DESCRIPTORS",
                        name: t?.name,
                        targetType: "INDIVIDUALS",
                        status: "NEW",
                    });
                });

                await this.actions.venture.saveVenture({
                    where: { id: this.$route.params?.ventureId },
                    data: {
                        targetCustomerUsers: {
                            update: [
                                {
                                    where: { id: this.$route.params?.id },
                                    data: {
                                        name: this.formFieldGroup[0].name.value,
                                        estMarketSize: parseInt(
                                            this.formFieldGroup[0]
                                                .estimatedMarketSize.value
                                        ),
                                        // estAnnualIncome: parseInt(
                                        // 	this.formFieldGroup[0]
                                        // 		.estimatedAnnualIncome.value
                                        // ),
                                        researchSource:
                                            this.formFieldGroup[0]
                                                .researchSource.value,
                                        notes: this.formFieldGroup[0].notes
                                            ?.value,
                                        // employeeSize: parseInt(
                                        // 	this.formFieldGroup[0].employeeSize
                                        // 		.value
                                        // ),
                                        targetCustomer: {
                                            update: {
                                                type: this.formFieldGroup[0]
                                                    ?.types.selected?.name,
                                                tags: {
                                                    connect: connects,
                                                    disconnect: disconnects,
                                                    create: create,
                                                },
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture.targetCustomerUsers?.find(
                        (t) => t?.id === this.targetCustomerUser?.id
                    );
                this.formFieldGroup[0].markets.value = [
                    ...this.targetCustomerUser.targetCustomer?.tags?.filter(
                        (t) => t.type === "LOCATION"
                    ),
                ];
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async onSaveInterview(interview, interviewScript) {
            if (this.isLoading) return;
            try {
                this.isLoading = true;
                this.savingPart = "Interview";
                await this.actions.interviewScript.saveInterviewScript({
                    where: { id: interviewScript?.id },
                    data: {
                        [interview.id]: interview.content,
                    },
                });
                await this.actions.milestone.calculateMilestone({
                    ventureId: this.$route.params?.ventureId,
                });
                await this.actions.interviewScript.updateDefaultInterviewScriptMilestone(
                    {
                        ventureId: this.$route.params?.ventureId,
                    }
                );
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture.targetCustomerUsers?.find(
                        (t) => t?.id === this.targetCustomerUser?.id
                    );
                this.selectedInterviewScript =
                    this.targetCustomerUser?.interviewScripts?.find(
                        (i) => i?.id === interviewScript?.id
                    );
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        getInterviewAccordions(interviewScript) {
            return [
                {
                    id: "introScript",
                    name: "Introduction",
                    content: interviewScript?.introScript,
                    type: "textArea",
                },
                {
                    id: "questionScript",
                    name: "Questions",
                    content: interviewScript?.questionScript,
                    type: "textArea",
                },
                {
                    id: "outroScript",
                    name: "Conclusion",
                    content: interviewScript?.outroScript,
                    type: "textArea",
                },
            ];
        },
        async saveForm(data, isMultipleContact) {
            try {
                if (this.isLoading) return false;
                this.isLoading = true;
                var contacts = isMultipleContact
                    ? data?.contacts.filter((o) => o?.isArchived != true)
                        ? [...data?.contacts]
                        : []
                    : [{ ...data }];
                var users = [];
                for (var i = 0; i < contacts.length; i++) {
                    let user = null;
                    if (contacts[i]?.email) {
                        const createAppUser = await this.actions.createAppUser({
                            email: contacts[i].email,
                            sendEmailVerification: false,
                        });
                        user = createAppUser?.user;
                        if (!user?.id) {
                            if (isMultipleContact) {
                                continue;
                            } else {
                                this.actions.alert.showError({
                                    mesasge: "User already exists",
                                });
                                break;
                            }
                        }
                    }
                    const contactData = {};
                    if (contacts[i]?.firstName) {
                        contactData["firstName"] = contacts[i].firstName;
                        contactData["username"] =
                            contacts[i].firstName?.toLowerCase();
                    }
                    if (contacts[i]?.lastName)
                        contactData["lastName"] = contacts[i].lastName;
                    if (contacts[i]?.phone)
                        contactData["phones"] = {
                            create: { number: contacts[i].phone },
                        };
                    const saveUser = await this.actions.user.saveUser({
                        where: user?.id ? { id: user.id } : null,
                        data: contactData,
                    });
                    users.push({ ...saveUser, ...contacts[i] });
                }
                console.log(users, "users");
                if (users.length > 0) {
                    const contacts = [];

                    users?.map((user) => {
                        const socialHandles = [];
                        if (isMultipleContact) {
                            if (user?.facebook)
                                socialHandles.push({
                                    platform: "FACEBOOK",
                                    handle: user?.facebook,
                                });
                            if (user?.twitter) {
                                socialHandles.push({
                                    platform: "TWITTER",
                                    handle: user?.twitter,
                                });
                            }
                            if (user?.instagram) {
                                socialHandles.push({
                                    platform: "INSTAGRAM",
                                    handle: user?.instagram,
                                });
                            }
                            if (user?.linkedin) {
                                socialHandles.push({
                                    platform: "LINKEDIN",
                                    handle: user?.linkedin,
                                });
                            }
                            if (user?.reddit) {
                                socialHandles.push({
                                    platform: "REDDIT",
                                    handle: user?.reddit,
                                });
                            }
                        } else {
                            if (data?.platform?.name && data?.socialLink) {
                                socialHandles.push({
                                    platform: data?.platform?.name,
                                    handle: data?.socialLink,
                                });
                                socialHandles.push({
                                    handle: data?.firstName,
                                    platform: data?.platform?.name,
                                });
                            } else if (
                                data?.platform?.name &&
                                !data?.socialLink
                            ) {
                                socialHandles.push({
                                    handle: data?.firstName,
                                    platform: data?.platform?.name,
                                });
                            }
                        }
                        this.actions.user.saveUser({
                            where: { id: user?.id },
                            data: {
                                socialHandles: { create: socialHandles },
                            },
                        });
                        contacts.push({
                            user: { connect: { id: user?.id } },
                            relationship: isMultipleContact
                                ? "STRANGER"
                                : data?.relationship?.value,
                            targetCustomerUser: {
                                connect: {
                                    id: data?.targetCustomer?.id,
                                },
                            },
                            status: "NEW_CONTACT",
                        });
                    });

                    const params = {
                        where: { id: this.$route.params?.ventureId },
                        data: {
                            contacts: {
                                create: contacts,
                            },
                        },
                    };
                    await this.actions.venture.saveVenture(params);
                    const ventures = await this.actions.venture.getVentures({
                        where: { id: this.$route.params?.ventureId },
                        getValues: true,
                        query: "ventureTargetCustomerDetail",
                    });
                    this.venture = ventures?.[0];
                    this.targetCustomerUser =
                        this.venture.targetCustomerUsers?.find(
                            (u) => u.id === this.$route.params?.id
                        );
                    this.getContacts();
                    const venture = await this.actions.venture.getVentureById(
                        this.$route.params?.ventureId
                    );
                    let surveyData = [];
                    for (
                        let i = 0;
                        i <
                        venture.targetCustomerUsers?.filter(
                            (t) => !t?.isArchived
                        )?.length;
                        i++
                    ) {
                        const targetCustomer =
                            venture.targetCustomerUsers?.filter(
                                (t) => !t?.isArchived
                            )[i];
                        const data =
                            await this.actions.userSurvey.getUserSurveys({
                                where: {
                                    isArchived_not: true,
                                    survey: {
                                        targetCustomerUser: {
                                            id: targetCustomer?.id,
                                        },
                                    },
                                },
                                getValues: true,
                            });
                        console.log(data, "data");
                        surveyData = [...surveyData, ...data];
                    }
                    venture.surveyData = surveyData;
                    this.actions.setCurrentVenture(venture);
                    this.isShow = false;
                    this.isShowImport = false;
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
            }
        },
        async saveOpportunityForm(data) {
            if (this.isSaving) return false;
            try {
                this.isSaving = true;
                const opportunityParams = {
                    problemContext: data?.problemContext,
                    type: "ACTIVITY",
                    targetCustomerUser: {
                        connect: { id: data?.targetCustomer?.id },
                    },
                    desireText: data?.motivation?.value,
                    problems: {
                        connect: [],
                        create: [],
                    },
                    name: "",
                };
                // data?.problem?.map((problem) => {
                if (!data?.problem?.id && data?.problem?.name) {
                    opportunityParams.problems.create.push({
                        type: "PROBLEM",
                        economy: {
                            create: {
                                name: data?.problem?.name,
                                problem: data?.problem?.name,
                            },
                        },
                    });
                } else if (data?.problem?.id) {
                    opportunityParams.problems.create.push({
                        type: "PROBLEM",
                        economy: { connect: { id: data?.problem?.id } },
                    });
                }
                // });
                if (data?.action?.id) {
                    opportunityParams.actionStep = {
                        connect: { id: data?.action?.id },
                    };
                } else if (data?.action?.label) {
                    opportunityParams.actionStep = {
                        create: {
                            name: data?.action?.label,
                            type: "ACTION",
                            status: "NEW",
                        },
                    };
                }
                await this.actions.venture.saveVenture({
                    where: { id: this.$route.params?.ventureId },
                    data: {
                        opportunities: {
                            create: [opportunityParams],
                        },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.getOpportunities();
                const venture = await this.actions.venture.getVentureById(
                    this.$route.params?.ventureId
                );
                let surveyData = [];
                for (
                    let i = 0;
                    i <
                    venture.targetCustomerUsers?.filter((t) => !t?.isArchived)
                        ?.length;
                    i++
                ) {
                    const targetCustomer = venture.targetCustomerUsers?.filter(
                        (t) => !t?.isArchived
                    )[i];
                    const data = await this.actions.userSurvey.getUserSurveys({
                        where: {
                            isArchived_not: true,
                            survey: {
                                targetCustomerUser: { id: targetCustomer?.id },
                            },
                        },
                        getValues: true,
                    });
                    console.log(data, "data");
                    surveyData = [...surveyData, ...data];
                }
                venture.surveyData = surveyData;
                this.actions.setCurrentVenture(venture);
                this.isShowOpportunity = false;
            } catch (e) {
                console.log(e);
            } finally {
                this.isSaving = false;
            }
        },
        getTabItems() {
            return [
                {
                    name: "Details",
                    count: null,
                    href: "#",
                    icon: "PencilIcon",
                    current: false,
                },
                {
                    name: "Opportunities",
                    count: this.opportunities?.length,
                    href: "#",
                    icon: "ExclamationIcon",
                    current: false,
                },
                {
                    name: "Contacts",
                    count: this.contacts?.filter((o) => !o?.isArchived)?.length,
                    href: "#",
                    icon: "UsersIcon",
                    current: false,
                },
                {
                    name: "Outreach",
                    count: null,
                    href: "#",
                    icon: "MailIcon",
                    current: false,
                },
                {
                    name: "Interviews",
                    count: this.interviews?.length,
                    href: "#",
                    icon: "ChatAlt2Icon",
                    current: false,
                },
                {
                    name: "Surveys",
                    count: this.targetCustomerUser?.surveys?.length,
                    href: "#",
                    icon: "ServerIcon",
                    current: true,
                },
            ];
        },
        onClickMenu(item) {
            // if (item === 'Archive') {
            this.isOpenRemove = true;
            // }
        },
        async onRemoveTargetCustomer() {
            await this.actions.venture.saveVenture({
                where: { id: this.venture?.id },
                data: {
                    targetCustomerUsers: {
                        delete: [{ id: this.targetCustomerUser?.id }],
                    },
                },
            });
            this.actions.alert.showSuccess({
                message: "Deleted the target customer successfully!",
            });
            this.isOpenRemove = false;
            this.$router.go(-1);
        },
        async onRemoveSurveyOpportunity() {
            await this.actions.survey.saveSurvey({
                where: { id: this.survey?.id },
                data: {
                    surveyOpportunities: {
                        disconnect: [
                            {
                                id: this.selectedSurveyOpportunity?.id,
                            },
                        ],
                    },
                },
            });
            await this.actions.milestone.calculateMilestone({
                ventureId: this.$route.params?.ventureId,
            });
            const index = this.survey.surveyOpportunities.findIndex(
                (o) => o?.id === this.selectedSurveyOpportunity?.id
            );
            this.survey.surveyOpportunities.splice(index, 1);
            this.isOpenRemoveOpportunity = false;
            const ventures = await this.actions.venture.getVentures({
                where: { id: this.$route.params?.ventureId },
                getValues: true,
                query: "ventureTargetCustomerDetail",
            });
            this.venture = ventures?.[0];
            this.targetCustomerUser = this.venture?.targetCustomerUsers?.find(
                (t) => t.id === this.targetCustomerUser?.id
            );
            this.survey = this.targetCustomerUser?.surveys?.find(
                (s) => s?.id === this.survey?.id
            );
            this.survey.surveyOpportunities =
                this.survey.surveyOpportunities?.sort((a, b) =>
                    a?.sortOrder > b?.sortOrder ? 1 : -1
                );
        },
        async onRemoveSurvey() {
            this.isOpenRemoveSurvey = false;
            await this.actions.survey.deleteSurvey({
                where: { id: this.survey?.id },
            });
            const index = this.targetCustomerUser?.surveys?.findIndex(
                (s) => s?.id === this.survey?.id
            );
            this.targetCustomerUser.surveys.splice(index, 1);
            this.survey = this.targetCustomerUser?.surveys?.[0];
            this.survey.surveyOpportunities =
                this.survey.surveyOpportunities?.sort((a, b) =>
                    a?.sortOrder > b?.sortOrder ? 1 : -1
                );
        },
        async onSaveSurveyName() {
            this.isEditSurveyName = false;
            await this.actions.survey.saveSurvey({
                where: { id: this.survey?.id },
                data: {
                    name: this.survey?.name,
                },
            });
            await this.actions.milestone.calculateMilestone({
                ventureId: this.$route.params?.ventureId,
            });
            await this.onInitialize();
            this.survey = this.targetCustomerUser?.surveys?.find(
                (s) => s.id === this.survey?.id
            );
            this.survey.surveyOpportunities =
                this.survey.surveyOpportunities?.sort((a, b) =>
                    a?.sortOrder > b?.sortOrder ? 1 : -1
                );
        },
        async onSaveScriptName(interviewScript) {
            this.isEditScriptName = false;
            const { saveVenture } = await this.actions.venture.saveVenture({
                where: { id: this.$route.params?.ventureId },
                data: {
                    targetCustomerUsers: {
                        update: [
                            {
                                where: { id: this.targetCustomerUser?.id },
                                data: {
                                    interviewScripts: {
                                        update: [
                                            {
                                                where: {
                                                    id: interviewScript?.id,
                                                },
                                                data: {
                                                    name: interviewScript?.name,
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    },
                },
            });
            const ventures = await this.actions.venture.getVentures({
                where: { id: this.$route.params?.ventureId },
                getValues: true,
                query: "ventureTargetCustomerDetail",
            });
            this.venture = ventures?.[0];
            this.targetCustomerUser = this.venture.targetCustomerUsers?.find(
                (t) => t?.id === this.targetCustomerUser?.id
            );
        },
        onPressScript() {
            document.getElementById(this.selectedInterviewScript?.id).blur();
        },
        onPressEnter() {
            document.getElementById("survey-name").blur();
        },
        onClickEditSurveyName() {
            this.isEditSurveyName = true;
            setTimeout(() => {
                document.getElementById("survey-name").focus();
            }, 100);
        },
        onEditScriptName(interviewScript) {
            this.isEditScriptName = true;
            setTimeout(() => {
                document.getElementById(interviewScript?.id).focus();
            }, 100);
        },
        async deleteSelectOption(item, question, surveyOpportunity) {
            try {
                if (this.isLoading) return false;
                this.isLoading = true;
                if (item?.[0]?.id) {
                    await this.actions.surveyOpportunity.saveSurveyOpportunity({
                        where: { id: surveyOpportunity?.id },
                        data: {
                            surveyQuestionGroup: {
                                update: {
                                    questions: {
                                        update: [
                                            {
                                                where: { id: question?.id },
                                                data: {
                                                    answerOptions: {
                                                        delete: [
                                                            {
                                                                id: item?.[0]
                                                                    ?.id,
                                                            },
                                                        ],
                                                    },
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                        },
                    });
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
            }
        },
        async onSaveResearch() {
            if (this.isLoading) return false;
            try {
                this.isLoading = true;
                this.savingPart = "Research";
                const { saveVenture } = await this.actions.venture.saveVenture({
                    where: { id: this.$route.params?.ventureId },
                    data: {
                        targetCustomerUsers: {
                            update: [
                                {
                                    where: { id: this.$route.params?.id },
                                    data: {
                                        researchPrompt: this.formData.research,
                                    },
                                },
                            ],
                        },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (u) => u?.id === this.$route.params?.id
                    );
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async onSaveSurveyOpportunity(surveyOpportunity) {
            console.log(surveyOpportunity, "surveyOpportunity");
            if (this.isLoading) return;
            try {
                this.isLoading = true;
                this.savingPart = "SurveyOpportunity";
                const questions = [];
                for (
                    let i = 0;
                    i <
                    surveyOpportunity?.surveyQuestionGroup?.questions?.length;
                    i++
                ) {
                    const q =
                        surveyOpportunity?.surveyQuestionGroup?.questions[i];
                    let update = [],
                        create = [];

                    if (q?.answerOptions?.length > 0) {
                        for (let j = 0; j < q?.answerOptions?.length; j++) {
                            const a = q?.answerOptions[j];
                            // console.log(a, a?.opportunityId, "abc s");
                            if (a?.id) {
                                var newAnswerName = a?.answer
                                    .replace("#", "")
                                    .replace(/^[\s\d]+/, "");

                                update?.push({
                                    where: { id: a?.id },
                                    data: {
                                        answer:
                                            newAnswerName
                                                .charAt(0)
                                                .toUpperCase() +
                                            newAnswerName.slice(1),
                                        metadata: {
                                            ...a?.metadata,
                                            ventureId: this.venture?.id,
                                            type: q?.category?.includes(
                                                "Solution"
                                            )
                                                ? "SOLUTION"
                                                : q?.category?.includes(
                                                      "Motivator"
                                                  )
                                                ? "MOTIVATOR"
                                                : "PROBLEM",
                                            economyId: q?.category?.includes(
                                                "Problem"
                                            )
                                                ? this.problems?.find(
                                                      (p) =>
                                                          p.name === a?.answer
                                                  )?.id
                                                : null,
                                        },
                                    },
                                });
                            } else {
                                if (
                                    !a?.opportunityId &&
                                    (q?.category?.includes("Solution") ||
                                        q?.category?.includes("Motivator"))
                                ) {
                                    // SOLUTION + MOTIVATOR
                                    const { saveOpportunity } =
                                        await this.actions.opportunity.saveOpportunity(
                                            {
                                                data: {
                                                    name: a?.answer,
                                                    type: q?.category?.includes(
                                                        "Solution"
                                                    )
                                                        ? "SOLUTION"
                                                        : "MOTIVATOR",
                                                    targetCustomerUser: {
                                                        connect: {
                                                            id: this
                                                                .targetCustomerUser
                                                                ?.id,
                                                        },
                                                    },
                                                    venture: {
                                                        connect: {
                                                            id: this.$route
                                                                .params
                                                                ?.ventureId,
                                                        },
                                                    },
                                                    actionStep: {
                                                        create: {
                                                            name: upperFirst(
                                                                a?.answer
                                                            ),
                                                            type: q?.category?.includes(
                                                                "Solution"
                                                            )
                                                                ? "SOLUTION"
                                                                : "MOTIVATOR",
                                                        },
                                                    },
                                                },
                                            }
                                        );

                                    console.log(
                                        saveOpportunity,
                                        "saveOpportunity"
                                    );

                                    var newAnswerName = saveOpportunity?.name
                                        .replace("#", "")
                                        .replace(/^[\s\d]+/, "");

                                    create?.push({
                                        answer:
                                            newAnswerName
                                                .charAt(0)
                                                .toUpperCase() +
                                            newAnswerName.slice(1),
                                        metadata: {
                                            opportunityName:
                                                saveOpportunity?.name,
                                            child: q?.category?.includes(
                                                "Solution"
                                            )
                                                ? saveOpportunity?.id
                                                : surveyOpportunity?.opportunity
                                                      ?.id,
                                            opportunityId: saveOpportunity?.id,
                                            parent: q?.category?.includes(
                                                "Motivator"
                                            )
                                                ? saveOpportunity?.id
                                                : surveyOpportunity?.opportunity
                                                      ?.id,
                                            ventureId: this.venture?.id,
                                            type: q?.category?.includes(
                                                "Solution"
                                            )
                                                ? "SOLUTION"
                                                : q?.category?.includes(
                                                      "Motivator"
                                                  )
                                                ? "MOTIVATOR"
                                                : "PROBLEM",
                                        },
                                    });

                                    this.actions.venture.saveVenture({
                                        where: {
                                            id: this.$route.params.ventureId,
                                        },
                                        data: {
                                            targetCustomerUsers: {
                                                update: [
                                                    {
                                                        where: {
                                                            id: this
                                                                .targetCustomerUser
                                                                ?.id,
                                                        },
                                                        data: {
                                                            opportunities: {
                                                                connect: [
                                                                    {
                                                                        id: saveOpportunity?.id,
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    },
                                                ],
                                            },
                                            opportunities: {
                                                connect: [
                                                    { id: saveOpportunity?.id },
                                                ],
                                            },
                                        },
                                    });
                                } else {
                                    // PROBLEM
                                    console.log("PROBLEM");
                                    console.log(a, "ANSWER");
                                    console.log(q, "QUESTION");

                                    if (
                                        a?.opportunityId &&
                                        q?.category?.includes("Problem")
                                    ) {
                                        create?.push({
                                            answer: a?.answer,
                                            metadata: {
                                                economyId: this.problems?.find(
                                                    (p) => p.name === a?.answer
                                                )?.id,
                                                ventureId: this.venture?.id,
                                                type: "PROBLEM",
                                                opportunityId:
                                                    surveyOpportunity
                                                        ?.opportunity?.id,
                                            },
                                        });
                                    } else {
                                        var newAnswerName = a?.answer
                                            .replace("#", "")
                                            .replace(/^[\s\d]+/, "");

                                        create?.push({
                                            answer:
                                                newAnswerName
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                newAnswerName.slice(1),
                                            metadata: {
                                                opportunityName:
                                                    q.selectedItems.find(
                                                        (i) =>
                                                            i.id ===
                                                            a?.opportunityId
                                                    )?.name,
                                                economyId: this.problems?.find(
                                                    (p) => p.name === a?.answer
                                                )?.id,
                                                ventureId: this.venture?.id,
                                                type: q?.category?.includes(
                                                    "Solution"
                                                )
                                                    ? "SOLUTION"
                                                    : q?.category?.includes(
                                                          "Motivator"
                                                      )
                                                    ? "MOTIVATOR"
                                                    : "PROBLEM",
                                                child: q?.category?.includes(
                                                    "Solution"
                                                )
                                                    ? a?.opportunityId
                                                    : surveyOpportunity
                                                          ?.opportunity?.id,
                                                opportunityId: a?.opportunityId,
                                                parent: q?.category?.includes(
                                                    "Motivator"
                                                )
                                                    ? a?.opportunityId
                                                    : surveyOpportunity
                                                          ?.opportunity?.id,
                                            },
                                        });
                                    }
                                }

                                // if (
                                //     a?.opportunityId &&
                                //     q?.category?.includes("Problem")
                                // ) {
                                //     this.actions.opportunity.saveOpportunity({
                                //         where: {
                                //             id: surveyOpportunity.opportunity
                                //                 ?.id,
                                //         },
                                //         data: {
                                //             problems: {
                                //                 create: [
                                //                     {
                                //                         type: "PROBLEM",
                                //                         economy: {
                                //                             connect: {
                                //                                 id: a?.opportunityId,
                                //                             },
                                //                         },
                                //                     },
                                //                 ],
                                //             },
                                //         },
                                //     });
                                // }
                            }
                        }
                    }
                    // console.log({
                    //             update,
                    //             create,
                    //         },'answerOptions')
                    questions.push({
                        where: { id: q?.id },
                        data: {
                            question: q?.question,
                            sortOrder: i,
                            answerOptions: {
                                update,
                                create,
                            },
                        },
                    });
                }
                await this.actions.surveyOpportunity.saveSurveyOpportunity({
                    where: { id: surveyOpportunity?.id },
                    data: {
                        surveyQuestionGroup: {
                            update: {
                                questions: {
                                    update: questions,
                                },
                            },
                        },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.survey = this.targetCustomerUser?.surveys?.find(
                    (s) => s?.id === this.survey?.id
                );
                this.actions.alert.showSuccess({
                    message: "Updated the opportunity successfully!",
                });
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async onCreateSurveyOpportunity() {
            if (
                this.selectedOpportunities?.length === 0 ||
                this.selectedOpportunities?.length >
                    3 - this.survey?.surveyOpportunities?.length
            )
                return false;
            if (this.isLoading) return false;
            try {
                this.isLoading = true;
                this.savingPart = "ADD_OPPORTUNITY";
                const surveyOpportunities = [];
                this.selectedOpportunities?.map((o, i) => {
                    const questions = [
                        {
                            category: "Heading",
                            inputType: "TEXTAREA",
                            question: `Let's talk about how you ${convertActionWord(
                                o?.actionStep?.name
                            )}.`,
                            sortOrder: 1,
                        },
                        {
                            category: "Opportunity done question",
                            inputType: "BOOLEAN",
                            question: `Just to double check, do you ${convertActionWord(
                                o?.actionStep?.name
                            )}?`,
                            sortOrder: 2,
                        },
                    ];
                    if (o?.frequency && o?.frequencyUnit) {
                        questions.push({
                            category: "Frequency question",
                            inputType: "NUMBER",
                            question: `How many times in the last ${o?.frequencyUnit?.toLowerCase()} did you ${convertActionWord(
                                o?.actionStep?.name
                            )}?`,
                            sortOrder: 3,
                        });
                    }
                    // if (o?.childOpportunityLinks?.length > 0) {
                    const answerOptions1 = [];
                    o?.childOpportunityLinks?.map((ol) =>
                        answerOptions1.push({
                            answer: convertActionWord(ol?.child?.name),
                            metadata: {
                                parent: o?.id,
                                ventureId: this.venture?.id,
                                child: ol?.child?.id,
                                opportunityId: ol?.child?.id,
                                type: "SOLUTION",
                            },
                        })
                    );
                    questions.push({
                        category: "Solutions and steps question",
                        subcategory: "Solutions and steps question",
                        inputType: "CHECKBOX",
                        question: `What are the different ways you ${convertActionWord(
                            o?.actionStep?.name
                        )}?`,
                        sortOrder: 4,
                        helpBlurb: "Check all that apply and/or add your own",
                        answerOptions: { create: answerOptions1 },
                    });
                    // }
                    questions.push({
                        category: "Desire question",
                        inputType: "SLIDER",
                        question: `How strong is your desire to ${convertActionWord(
                            o?.actionStep?.name
                        )}?`,
                        sortOrder: 5,
                        helpBlurb:
                            "Follow the indicators below and drag to what matches your experience the most.",
                    });
                    // if (o?.parentOpportunityLinks?.length > 0) {
                    const answerOptions = [];
                    o?.parentOpportunityLinks?.map((ol) =>
                        answerOptions.push({
                            answer: convertActionWord(ol?.parent?.name),
                            metadata: {
                                parent: ol?.parent?.id,
                                ventureId: this.venture?.id,
                                opportunityId: ol?.parent?.id,
                                child: o?.id,
                                type: "MOTIVATOR",
                            },
                        })
                    );
                    questions.push({
                        category: "Motivators question",
                        subcategory: "Motivators question",
                        inputType: "CHECKBOX",
                        question: `What are the main reasons why you ${convertActionWord(
                            o?.actionStep?.name
                        )}?`,
                        sortOrder: 6,
                        helpBlurb: "Check all that apply and/or add your own",
                        answerOptions: { create: answerOptions },
                    });
                    // }
                    questions.push({
                        category: "Experience question",
                        inputType: "SLIDER",
                        question: `How would you describe your experience with how you ${convertActionWord(
                            o?.actionStep?.name
                        )}?`,
                        sortOrder: 7,
                    });
                    const problems = [];
                    o?.problems?.map((p) =>
                        problems.push({
                            answer: upperFirst(p?.economy?.problem),
                            metadata: {
                                ventureId: this.venture?.id,
                                type: "PROBLEM",
                                economyId: p?.id,
                            },
                        })
                    );
                    questions.push({
                        category: "Problems question",
                        inputType: "CHECKBOX",
                        question: `What are the biggest problems you have with how you ${convertActionWord(
                            o?.actionStep?.name
                        )}?`,
                        sortOrder: 8,
                        helpBlurb: "Check all that apply and/or add your own",
                        answerOptions: {
                            create: problems,
                        },
                    });

                    console.log(questions, "questions");

                    surveyOpportunities.push({
                        opportunity: { connect: { id: o?.id } },
                        surveyQuestionGroup: {
                            create: {
                                questions: {
                                    create: questions,
                                },
                            },
                        },
                        sortOrder: this.survey?.surveyOpportunities?.length + i,
                    });
                });
                await this.actions.survey.saveSurvey({
                    where: { id: this.survey?.id },
                    data: {
                        surveyOpportunities: {
                            create: surveyOpportunities,
                        },
                    },
                });
                await this.actions.milestone.calculateMilestone({
                    ventureId: this.$route.params?.ventureId,
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.survey = this.targetCustomerUser?.surveys?.find(
                    (s) => s?.id === this.survey?.id
                );
                this.survey.surveyOpportunities =
                    this.survey.surveyOpportunities?.sort((a, b) =>
                        a?.sortOrder > b?.sortOrder ? 1 : -1
                    );
                this.currentOpportunityIndex = json(
                    this.survey.surveyOpportunities
                )?.reverse()?.[0]?.id;
                setTimeout(() => {
                    document
                        .getElementById(this.currentOpportunityIndex)
                        .scrollIntoView();
                }, 100);
            } catch (e) {
                console.log(e);
            } finally {
                this.isShowOpportunitySelect = false;
                this.isLoading = false;
                this.savingPart = null;
                this.selectedOpportunities = [];
            }
        },
        onSelectItems(item, question) {
            const items = question.selectedItems || [];
            const index = items?.findIndex((o) => o.id === item?.id);
            if (index > -1) {
                items?.splice(index, 1);
            } else {
                items.push(item);
            }
            question.selectedItems = items;
        },
        async onSaveSurvey() {
            if (this.isLoading) return;
            try {
                this.isLoading = true;
                this.savingPart = "Survey";
                await this.actions.survey.saveSurvey({
                    where: { id: this.survey?.id },
                    data: {
                        welcomeHeading: this.survey?.welcomeHeading,
                        welcomeDescription: this.survey?.welcomeDescription,
                        closingHeading: this.survey?.closingHeading,
                        closingDescription: this.survey?.closingDescription,
                    },
                });
                await this.actions.milestone.calculateMilestone({
                    ventureId: this.$route.params?.ventureId,
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.survey = this.targetCustomerUser?.surveys?.find(
                    (s) => s?.id === this.survey?.id
                );
                this.survey.surveyOpportunities =
                    this.survey.surveyOpportunities?.sort((a, b) =>
                        a?.sortOrder > b?.sortOrder ? 1 : -1
                    );
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async onAddSurvey() {
            if (this.isLoading) return false;
            try {
                this.isLoading = true;
                this.savingPart = "ADD_SURVEY";
                const { saveSurvey } = await this.actions.survey.saveSurvey({
                    data: {
                        name: `${moment().format("MM/DD")} Survey`,
                        welcomeHeading:
                            `Thank you so much for taking the time to help us learn how ${this.targetCustomerUser?.name?.toLowerCase()} like you ` +
                            this.targetCustomerUser?.researchPrompt,
                        welcomeDescription:
                            "This should only take a minute so let's get started!",
                        closingHeading:
                            "Thank you so much for taking the time to complete our survey.",
                        closingDescription: `Please don't hesitate to email us with any question or comments at ${
                            this.venture?.createdBy?.email
                        }<br><br><br>If you know any other ${this.targetCustomerUser?.name?.toLowerCase()} who would be willing to share their thoughts, we'd be grateful if you'd pass this link along.`,
                    },
                });
                await this.actions.survey.saveSurvey({
                    where: { id: saveSurvey?.id },
                    data: {
                        publicUrl:
                            `${Settings.mainDomain}/#/survey/` + saveSurvey?.id,
                    },
                });
                await this.actions.milestone.calculateMilestone({
                    ventureId: this.$route.params?.ventureId,
                });
                await this.actions.venture.saveVenture({
                    where: { id: this.$route.params?.ventureId },
                    data: {
                        targetCustomerUsers: {
                            update: [
                                {
                                    where: { id: this.targetCustomerUser?.id },
                                    data: {
                                        surveys: {
                                            connect: [{ id: saveSurvey?.id }],
                                        },
                                    },
                                },
                            ],
                        },
                        surveys: { connect: [{ id: saveSurvey?.id }] },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                console.log(this.venture, "venture");
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (t) => t.id === this.targetCustomerUser?.id
                    );
                this.survey = this.targetCustomerUser?.surveys?.find(
                    (s) => s?.id === saveSurvey?.id
                );
                this.survey.surveyOpportunities =
                    this.survey.surveyOpportunities?.sort((a, b) =>
                        a?.sortOrder > b?.sortOrder ? 1 : -1
                    );
                this.actions.alert.showSuccess({
                    message: "Added a survey successfully!",
                });
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        async onSaveEmailTemplate() {
            if (this.isLoading) return false;
            const update = [];
            this.tabs.map((tab) => {
                update.push({
                    where: { id: tab?.id },
                    data: {
                        body: tab?.body,
                        subject: tab?.subject,
                    },
                });
            });
            try {
                if (this.isLoading) return;
                this.isLoading = true;
                this.savingPart = "EmailTemplate";
                const { saveVenture } = await this.actions.venture.saveVenture({
                    where: { id: this.$route.params?.ventureId },
                    data: {
                        targetCustomerUsers: {
                            update: [
                                {
                                    where: { id: this.$route.params?.id },
                                    data: {
                                        emailTemplates: {
                                            update,
                                        },
                                    },
                                },
                            ],
                        },
                    },
                });
                const ventures = await this.actions.venture.getVentures({
                    where: { id: this.$route.params?.ventureId },
                    getValues: true,
                    query: "ventureTargetCustomerDetail",
                });
                this.venture = ventures?.[0];
                this.targetCustomerUser =
                    this.venture?.targetCustomerUsers?.find(
                        (u) => u?.id === this.$route.params?.id
                    );
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
                this.savingPart = null;
            }
        },
        getDemographicQuestion(tag) {
            const questions = c.demographicQuestions;
            if (tag?.category === "AGE") {
                return questions[0]?.label;
            } else if (tag?.category === "GENDER") {
                return questions[1]?.label;
            } else if (tag?.category === "ETHNICITY") {
                return questions[2]?.label;
            } else if (
                tag?.category === "DESCRIPTORS" ||
                tag?.category === "DESCRIPTORS"
            ) {
                return questions[5]?.label;
            } else if (tag?.category === "INCOME") {
                return questions[4]?.label;
            } else if (tag?.type === "LOCATION") {
                return questions[3]?.label;
            }
        },
        getPreviewDemographicText(tag) {
            if (tag?.category === "AGE") {
                return `Are you between the ages of ${tag?.name}`;
            } else if (tag?.category === "GENDER") {
                return `Are you a ${tag?.name}`;
            } else if (tag?.category === "ETHNICITY") {
                return `Is your ethnicity either ${tag.name}`;
            } else if (tag?.category === "DESCRIPTORS") {
                return `Are you in ${tag.name}`;
            } else if (tag?.category === "INCOME") {
                let item = tag?.name?.split("(");
                if (item?.[1]) {
                    item = item?.[1]?.split(")")[0];
                }
                return `Is your income ${item}`;
            }
        },
        getCompleteSurveys() {
            const items = [];
            this.survey?.userSurveys?.map((response) => {
                if (
                    response?.isArchived !== true &&
                    !items?.find((item) => item?.id === response?.profile?.id)
                ) {
                    items.push({
                        Contact: `${response?.user?.firstName || ""} ${
                            response?.user?.lastName || ""
                        }`,
                        "Response Date": moment(response?.createdAt).format(
                            "MM/DD/YY"
                        ),
                        Status: convertEnumToRead(response?.profile?.status),
                        id: response?.profile?.id,
                    });
                }
            });
            console.log(items, " items", this.survey);
            this.completedSurveys = items;
        },
        async getOpportunities() {
            const allUserSurveys = await this.actions.userSurvey.getUserSurveys(
                {
                    where: {
                        opportunity: {
                            venture: { id: this.$route.params?.ventureId },
                        },
                        isArchived_not: true,
                    },
                    getValues: true,
                }
            );
            try {
                const opportunities = [];
                for (
                    let i = 0;
                    i <
                    this.targetCustomerUser?.opportunities?.filter(
                        (o) => !o?.isArchived
                    )?.length;
                    i++
                ) {
                    const o = this.targetCustomerUser?.opportunities?.filter(
                        (o) => !o?.isArchived
                    )[i];
                    const userSurveys = allUserSurveys.filter(
                        (u) => u?.opportunity?.id === o?.id
                    );
                    const subtitles = [
                        {
                            icon: "MapIcon",
                            name: this.targetCustomerUser?.name,
                        },
                    ];
                    if (
                        (o?.childOpportunityLinks?.length || 0) +
                            (o?.parentOpportunityLinks?.length || 0) >
                        0
                    ) {
                        subtitles.push({
                            icon: "LinkIcon",
                            name: "Linked Opps",
                            count:
                                (o?.childOpportunityLinks?.length || 0) +
                                (o?.parentOpportunityLinks?.length || 0),
                        });
                    }
                    if (o?.interviews?.length > 0) {
                        subtitles.push({
                            icon: "ChatIcon",
                            name: "Interview",
                            count:
                                o?.interviews?.filter((o) => !o?.isArchived)
                                    ?.length || 0,
                        });
                    }
                    if (userSurveys?.length > 0) {
                        subtitles.push({
                            icon: "TrendingUpIcon",
                            name: "Responses",
                            count: userSurveys?.length,
                        });
                    }
                    let surveyParam = {};
                    if (userSurveys?.length > 0) {
                        if (o?.magnitudeScore)
                            surveyParam["Magnitude"] = {
                                out: o?.outOfMarketPercentage || 0,
                                low: o?.lowMarketPercentage || 0,
                                mid: o?.midMarketPercentage || 0,
                                hi: o?.hiMarketPercentage || 0,
                                magnitude: o?.magnitudeScore,
                            };
                        if (o?.actionDonePercentage > 0)
                            surveyParam[
                                "Action Done"
                            ] = `${o?.actionDonePercentage}%`;
                        if (o?.avgFrequency && o?.frequencyUnit)
                            surveyParam["Frequency"] = `${
                                o?.avgFrequency || ""
                            } ${
                                c.frequencyOptions?.find(
                                    (f) => f?.value === o?.frequencyUnit
                                )?.name || ""
                            }`;
                        if (o?.desireScore)
                            surveyParam["Desire"] = o?.desireScore;
                        if (o?.experience)
                            surveyParam["Experience"] = o?.experience;
                        if (o?.adjustedMarket)
                            surveyParam["Market"] = o?.adjustedMarket;
                    } else {
                        surveyParam = null;
                    }
                    opportunities?.push({
                        title: o.name,
                        actionLabel: o?.type,
                        id: o?.id,
                        detail: o,
                        subtitles,
                        survey: surveyParam,
                        customClass: "",
                    });
                }
                this.opportunities = opportunities.sort((a, b) =>
                    a?.detail?.magnitudeScore > b?.detail?.magnitudeScore
                        ? -1
                        : 1
                );
                this.motivators = this.targetCustomerUser.opportunities?.filter(
                    (o) => !o?.isArchived
                );
                this.motivators.map((item) => {
                    if (item.name.includes("#")) {
                        const items = item.name.split(" ");
                        items.shift();
                        item.label = items.join(" ");
                        // item.name = items.join(" ");
                    } else {
                        item.label = item.name;
                    }
                });
                this.solutions = this.targetCustomerUser.opportunities?.filter(
                    (o) => !o?.isArchived
                );
                this.solutions.map((item) => {
                    if (item.name.includes("#")) {
                        const items = item.name.split(" ");
                        items.shift();
                        item.label = items.join(" ");
                        // item.name = items.join(" ");
                    } else {
                        item.label = item.name;
                    }
                });
                console.log(this.motivators, this.solutions, "solutions");
            } catch (e) {
                console.log(e);
            }
        },
        async onInitialize() {
            const ventures = await this.actions.venture.getVentures({
                where: { id: this.$route.params?.ventureId },
                getValues: true,
                query: "ventureTargetCustomerDetail",
            });
            this.venture = ventures[0];
            this.targetCustomerUser = this.venture.targetCustomerUsers?.find(
                (u) => u.id === this.$route.params?.id
            );
            this.surveyData = await this.actions.userSurvey.getUserSurveys({
                where: {
                    isArchived_not: true,
                    survey: {
                        targetCustomerUser: { id: this.targetCustomerUser?.id },
                    },
                },
                getValues: true,
            });
            this.contactForm[0].fields[5].options = JSON.stringify(
                this.venture.targetCustomerUsers?.filter(
                    (t) => !t?.isArchived
                ) || []
            );
        },
        showContactInfo(user) {
            let emails = "";
            user?.emails?.map((e) => (emails += `<div>${e.address}</div>`));
            if (user?.emails?.length > 0) {
                return `<div>${emails}</div>`;
            } else if (
                user?.socialHandles?.length > 0 &&
                user?.emails?.length === 0
            ) {
                let links = "";
                user?.socialHandles?.map(
                    (s) => (links += `<div>${s?.platform} - ${s?.handle}</div>`)
                );
                return links;
            } else if (user?.username) {
                return user?.username;
            } else {
                return "";
            }
        },
        getContacts() {
            // Contact form
            this.contacts = [];
            this.headers = [
                { label: "Full Name", isSort: true, isAscending: true },
                { label: "Target Customer", isFilter: true },
                { label: "Contact Info", isSort: true, isAscending: true },
                { label: "Relationship", isFilter: true },
                { label: "Status", isFilter: true },
                { label: "Interview Date", isSort: true, isAscending: true },
            ];
            this.targetCustomerUser?.contacts
                ?.filter((o) => !o?.isArchived)
                ?.map((contact) => {
                    let emails = "";
                    contact.user?.emails?.map(
                        (e) => (emails += `<div>${e.address}</div>`)
                    );
                    this.contacts.push({
                        "Full Name": `${contact?.user?.firstName || ""} ${
                            contact?.user?.lastName || ""
                        }`,
                        "Target Customer": this.targetCustomerUser?.name,
                        "Contact Info": this.showContactInfo(contact?.user),
                        Relationship: convertEnumToRead(contact?.relationship),
                        Status: convertEnumToRead(contact?.status),
                        "Interview Date": contact?.interviews?.[0]
                            ?.scheduledDateTime
                            ? moment(
                                  contact?.interviews?.[0]?.scheduledDateTime
                              ).format("M/DD @ h:mm")
                            : "",
                        id: contact?.id,
                    });
                });
        },
        async saveOutreachPlan() {
            console.log("💾 Saving outreach plan...");

            try {
                const budgetData = {
                    interviewRewardAmount:
                        parseFloat(this.interviewReward) || 0,
                    interviewRewardMax: parseInt(this.maxInterviewRewards) || 0,
                    interviewBudget: this.interviewBudget,
                    surveyRewardAmount: parseFloat(this.surveyReward) || 0,
                    surveyRewardMax: parseInt(this.maxSurveyRewards) || 0,
                    surveyBudget: this.surveyBudget,
                };

                console.log("📊 Budget data:", budgetData);

                const result =
                    await this.actions.venture.saveTargetCustomerUser({
                        data: budgetData,
                        where: { id: this.targetCustomerUser.id },
                    });

                console.log("✅ Outreach plan saved:", result);

                // Update the local targetCustomerUser with the saved data
                this.targetCustomerUser = {
                    ...this.targetCustomerUser,
                    ...budgetData,
                    contacts: result.saveTargetCustomerUser.contacts,
                };

                console.log(
                    "🔄 Updated targetCustomerUser:",
                    this.targetCustomerUser
                );
            } catch (error) {
                console.error("❌ Error saving outreach plan:", error);
            }
        },
    },
    async created() {
        this.isPageLoading = true;
        this.selectedNav = this.getTabItems()[0];
        await this.onInitialize();

        // Load contact search state from database
        this.loadContactSearchState();

        try {
            const { getDemographicDataCharts: summary } =
                await this.actions.targetCustomer.getDemographicDataCharts({
                    targetCustomerUserId: this.targetCustomerUser?.id,
                });
            this.demographics = summary?.graphs;
        } catch (e) {
            console.log(e);
        }

        console.log(this.targetCustomerUser, "query targetCustomerUser");

        this.selectedInterviewScript = json(
            this.targetCustomerUser?.interviewScripts
        )?.reverse()[0];
        this.tabs = this.targetCustomerUser?.emailTemplates;

        // Initialize outreach reward values
        this.interviewReward =
            this.targetCustomerUser?.interviewRewardAmount?.toString() || "20";
        this.maxInterviewRewards =
            this.targetCustomerUser?.interviewRewardMax?.toString() || "20";
        this.surveyReward =
            this.targetCustomerUser?.surveyRewardAmount?.toString() || "0";
        this.maxSurveyRewards =
            this.targetCustomerUser?.surveyRewardMax?.toString() || "30";

        console.log("🔄 Initialized outreach reward values:", {
            interviewReward: this.interviewReward,
            maxInterviewRewards: this.maxInterviewRewards,
            surveyReward: this.surveyReward,
            maxSurveyRewards: this.maxSurveyRewards,
            interviewBudget: this.interviewBudget,
            surveyBudget: this.surveyBudget,
        });

        // Check for opportunity map query parameter
        this.openOpportunityMapFromUrl();
        this.tabs?.map((t) => (t.origBody = t.body));
        if (this.$route.query?.emailTemplate) {
            document.getElementById("outreach_template").scrollIntoView();
            this.selectedTab = this.tabs?.find(
                (tab) => tab.id === this.$route.query?.emailTemplate
            );
        } else {
            this.selectedTab = this.tabs[0];
        }

        if (this.$route.query?.tab === "Interview") {
            this.selectedNav = this.getTabItems()[4];
        }
        this.editor = new Editor({
            content: this.selectedTab?.body,
            extensions: [StarterKit, BulletList, OrderedList, ListItem],
            onUpdate: (val) => {
                this.selectedTab.body = this.editor.getHTML();
            },
        });
        const interviews = [];
        this.targetCustomerUser?.contacts
            ?.filter((o) => !o?.isArchived)
            ?.map((c) => {
                c?.interviews
                    ?.filter((o) => !o?.isArchived)
                    ?.map((interview) => {
                        if (
                            interview?.opportunities?.filter(
                                (o) => !o?.isArchived
                            )?.length > 0
                        ) {
                            interviews.push({
                                Contact: `${c?.user?.firstName || ""} ${
                                    c?.user?.lastName || ""
                                }`,
                                "Interview Date": moment(
                                    interview.scheduledDateTime
                                ).format("MM/DD @ h:mm"),
                                "Linked Opportunities":
                                    interview?.opportunities?.filter(
                                        (o) => !o?.isArchived
                                    )?.length,
                                id: c?.id,
                            });
                        }
                    });
            });
        this.interviews = interviews;
        this.survey = this.targetCustomerUser?.surveys[0];
        if (this.survey?.id) {
            this.survey.surveyOpportunities =
                this.survey?.surveyOpportunities?.sort((a, b) =>
                    a?.sortOrder > b?.sortOrder ? 1 : -1
                );
        }

        console.log(this.survey, "survey");
        this.opportunities = [];
        this.getOpportunities();
        this.getCompleteSurveys();
        this.opItems = this.targetCustomerUser?.opportunities?.filter(
            (o) => !o?.isArchived
        );
        this.getContacts();
        this.formData.targetCustomer = this.targetCustomerUser;
        this.activeIndex = 0;
        try {
            this.formFieldGroup[0].name.value = this.targetCustomerUser?.name;
            this.formFieldGroup[0].types.selected =
                this.formFieldGroup[0].types.list.find(
                    (l) =>
                        l.name === this.targetCustomerUser.targetCustomer?.type
                );
            await this.actions.tag.getTags({
                all: true,
                where: { status: "APPROVED" },
            });
            // this.formFieldGroup[0].age.value = [
            //     ...this.targetCustomerUser.targetCustomer?.tags?.filter(
            //         (t) => t.category === "AGE"
            //     ),
            // ];
            this.formFieldGroup[0].age.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "AGE"
                );
            this.formFieldGroup[0].age.list = [
                ...this.state.tag?.tags?.filter((t) => t.category === "AGE"),
            ];
            // this.formFieldGroup[0].gender.value = [
            //     ...this.targetCustomerUser.targetCustomer?.tags?.filter(
            //         (t) => t.category === "GENDER"
            //     ),
            // ];
            this.formFieldGroup[0].gender.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "GENDER"
                );
            this.formFieldGroup[0].gender.list = [
                ...this.state.tag?.tags?.filter((t) => t.category === "GENDER"),
            ];
            // this.formFieldGroup[0].ethnicity.value = [
            //     ...this.targetCustomerUser.targetCustomer?.tags?.filter(
            //         (t) => t.category === "ETHNICITY"
            //     ),
            // ];
            this.formFieldGroup[0].ethnicity.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "ETHNICITY"
                );
            this.formFieldGroup[0].ethnicity.list = [
                ...this.state.tag?.tags?.filter(
                    (t) => t.category === "ETHNICITY"
                ),
            ];
            // this.formFieldGroup[0].income.value = [
            //     ...this.targetCustomerUser.targetCustomer?.tags?.filter(
            //         (t) => t.category === "INCOME"
            //     ),
            // ];
            this.formFieldGroup[0].income.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "INCOME"
                );
            this.formFieldGroup[0].income.list = [
                ...this.state.tag?.tags?.filter((t) => t.category === "INCOME"),
            ];
            this.formFieldGroup[0].interestAndActivities.value = [
                ...this.targetCustomerUser.targetCustomer?.tags?.filter(
                    (t) => t.category === "DESCRIPTORS"
                ),
            ];
            this.formFieldGroup[0].interestAndActivities.list = [
                ...this.state.tag?.tags?.filter(
                    (t) => t.category === "DESCRIPTORS"
                ),
            ];
            this.formFieldGroup[0].markets.value = [
                ...this.targetCustomerUser.targetCustomer?.tags?.filter(
                    (t) => t.type === "LOCATION"
                ),
            ];
            this.formFieldGroup[0].estimatedAnnualIncome.list = [
                ...this.state.tag.tags?.filter((t) => t.category === "REVENUE"),
            ];
            this.formFieldGroup[0].estimatedAnnualIncome.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "REVENUE"
                );
            this.formFieldGroup[0].employeeSize.list = [
                ...this.state.tag.tags?.filter(
                    (t) => t.category === "EMPLOYEE_COUNT"
                ),
            ];
            this.formFieldGroup[0].employeeSize.value =
                this.targetCustomerUser.targetCustomer?.tags?.find(
                    (t) => t.category === "EMPLOYEE_COUNT"
                );
            this.formFieldGroup[0].estimatedMarketSize.value =
                this.targetCustomerUser.estMarketSize || 0;
            this.formFieldGroup[0].researchSource.value =
                this.targetCustomerUser?.researchSource;
            this.formFieldGroup[0].notes.value = this.targetCustomerUser?.notes;
            this.formData.research = this.targetCustomerUser?.researchPrompt;

            // Add Opportunity Form
            if (this.state.actionStep.actionSteps?.length === 0) {
                await this.actions.actionStep.getActionSteps({ all: true });
            }
            const actionSteps = [];
            this.state.actionStep.actionSteps
                ?.filter((a) => a?.status === "APPROVED")
                .map((o) => actionSteps?.push({ ...o, label: o?.name }));
            if (this.state.economy.economies?.length === 0) {
                await this.actions.economy.getEconomies({ all: true });
            }
            const economies = [];
            this.state.economy.economies?.map((o) =>
                economies?.push({
                    ...o,
                    name: upperFirst(o?.problem),
                    label: upperFirst(o?.problem),
                })
            );
            this.problems = economies;
            this.opportunityForm[0].fields[1].options = JSON.stringify(
                this.venture.targetCustomerUsers?.filter((t) => !t?.isArchived)
            );
            this.opportunityForm[0].fields[3].options =
                JSON.stringify(actionSteps);
            this.opportunityForm[0].fields[5].options = JSON.stringify(
                economies?.filter((e) => e?.name)
            );
        } catch (e) {
            console.log(e);
        }
        this.isPageLoading = false;
    },
};
</script>

<style>
ul {
    list-style-type: disc;
    padding: 10px 0 !important;
    padding-left: 20px !important;
}

ol {
    list-style-type: decimal;
    padding: 10px 0 !important;
    padding-left: 20px !important;
}
</style>
